import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import CreateNewWebApplicationPopup from "./web_application/Create_New_web_appliaction_Popup";
import DraftCard from "../../ui/draftCard";
import FeedbackCard from "../../ui/feedbackCard";
import DeveloperFeedbackCard from "../../ui/DeveloperFeedbackCard";
import ShareFeedbackPopup from "../../ui/ShareFeedbackPopup";
import { ScreenshotSubmission } from "../../ui/screenshot-screenrecord/Screenshot/Screenshot_Preview";
import { RecordingSubmission } from "../../ui/screenshot-screenrecord/screen-recording/ScreenRecordingPreview";
import { shareFeedbackWithDeveloper, deleteCard } from "../../../api/api-utils";
import { CardHoverWrapper } from "../../ui/CardHoverEffect";
import { DEFAULT_FONT_FAMILY } from "../../../config/fontConfig";
import {
  getVideoThumbnail,
  API_URLS,
  BASE_URL,
  getTourGuideStatus,
  markTourGuideDone,
  TOUR_GUIDE_KEYS,
} from "../../../api/urls";
import {
  EnhancedTokenManager,
  fetchUserProfileAndWorkspace,
} from "../../../utils/tokenManager";
import { DeveloperTokenManager } from "../../../utils/developerTokenManager";
import { pipStore } from "../../../utils/pipStore";
import {
  getWorkspaceIdWithFallback,
  shouldRedirectToOnboarding,
  canAccessDashboard,
  debugWorkspaceStatus,
} from "../../../utils/workspaceHelpers";
import AvatarIcon from "../../../assets/images/profile-avatar.svg";
import { ReactComponent as Mobile } from "../../../assets/icons/svg/Home/mobile.svg";
import { ReactComponent as Web } from "../../../assets/icons/svg/Home/web.svg";
import { ReactComponent as Figma } from "../../../assets/icons/svg/Home/figma.svg";
import { ReactComponent as Upload } from "../../../assets/icons/svg/Home/upload.svg";
import { ReactComponent as Logo } from "../../../assets/icons/svg/Home/Logo.svg";
import { ReactComponent as AddWeb } from "../../../assets/icons/svg/Home/add-web.svg";
import { ReactComponent as AddFigma } from "../../../assets/icons/svg/Home/add-figma.svg";

import MobileAnimation from "../../../assets/Animation/Stars_Sparkle_mobile.json";
import FigmaAnimation from "../../../assets/Animation/Stars_Sparkle_figma.json";
import MinimizedWorkspace from "../../ui/MinimizedWorkspace";
import { SubdomainManager } from "../../../utils/subdomainUtils";
import { buildCardUrl } from "../../../utils/urlIdEncoder";
import { useProfileImage } from "../../../contexts/ProfileImageContext";
import CreateNewUplaodFromDevicePopup from "./upload_from_device/Create_New_Uplaod_From_Device_Popup";
import BetaVersionPopup from "./BetaVersionPopup";
import UploadQueueManager, {
  UploadQueueItem,
} from "../../../utils/uploadQueueManager";

// Keep all existing interfaces unchanged...
interface DraftFeedbackItem {
  id: number;
  name: string;
  workspace_id: number;
  card_id: number;
  created_by: number;
  url: string;
  screenshot_url: string | null;
  recording_url: string | null;
  voice_recording_url: string | null;
  message: string | null;
  status: "draft";
  created_at: string;
}

interface SharedFeedbackItem {
  feedback: {
    id: number;
    name: string;
    workspace_id: number;
    card_id: number;
    created_by: number;
    url: string;
    screenshot_url: string | null;
    recording_url: string | null;
    voice_recording_url: string | null;
    message: string | null;
    status: "sent" | "draft";
    updated_status: "invited" | "viewed" | "in_progress" | "done" | "completed" | null;
    created_at: string;
    profile_image?: string | null;
    creator_name?: string | null;
  };
  sent_at: string;
}
interface DeveloperFeedbackItem {
  id: number;
  screenshot_url: string | null;
  recording_url: string | null;
  voice_recording_url: string | null;
  message: string | null;
  created_at: string;
  sent_at: string | null;
  profile_image?: string | null;
  creator_name?: string | null;
}

interface DeveloperFeedbackCard {
  card_id: number;
  card_name: string;
  feedbacks: DeveloperFeedbackItem[];
}
interface Developer {
  email: string;
  name: string | null;
  profile_image: string | null;
}
interface DraftCardData {
  card_id: number;
  card_name: string;
  preview_screenshot: string | null;
  feedbacks: DraftFeedbackItem[];
}

interface SharedCardData {
  card_id: number;
  card_name: string;
  preview_screenshot: string | null;
  feedbacks: SharedFeedbackItem[];
}

interface DraftApiResponse {
  [cardName: string]: DraftCardData;
}

interface SharedApiResponse {
  [cardName: string]: SharedCardData;
}

interface FeedbackData {
  name: string;
  collaborators: string[];
  accessType: "comment" | "edit";
}

interface CreateCardApiResponse {
  id: number;
  name: string;
  created_at: string;
}

interface FeedbackItem {
  id: string;
  type: "screenshot" | "recording";
  timestamp: number;
  order: number;
  annotatedImage?: string;
  originalImage?: string;
  feedback?: string;
  dataUrl?: string;
  recordingBlob?: Blob;
  duration?: number;
  hasAudio?: boolean;
  audioUrl?: string;
  audioFilename?: string;
  question?: string;
  user?: {
    name: string;
    avatar: string;
  };
  isSelected?: boolean;
  apiFeedbackId?: number;
  uniqueFeedbackId?: string;
  thumbnailUrl?: string;
  profileImage?: string;
  creatorName?: string;
  developerStatus?: "acknowledged" | "unclear" | "reshare" | null; // ✅ ADD THIS LINE
}

interface WorkspaceState {
  screenshots: ScreenshotSubmission[];
  recordings: RecordingSubmission[];
  feedbackItemsOrder: string[];
  url?: string;
  iframeHistory?: string[];
  iframeHistoryIndex?: number;
  showIframe?: boolean;
  feedbackId?: string;
  isUrlLocked?: boolean;
  shouldRefreshDrafts?: boolean; // ✅ NEW: Flag to trigger draft refresh
}
interface EnhancedDraftCardData {
  id: number;
  name: string;
  created_at: string;
  imageUrl: string;
  feedbackCount: number;
  feedbackData: FeedbackData;
  savedFeedbackItems?: FeedbackItem[];
  savedWorkspaceState?: WorkspaceState;
  status: "draft";
  lastUpdated?: string;
  allApiItems?: DraftFeedbackItem[];
}

interface EnhancedSharedFeedbackCard {
  id: number;
  name: string;
  created_at: string;
  imageUrl: string;
  feedbackCount: number;
  status: "Invited" | "Viewed" | "In progress" | "Done" | "Shared with me";
  sharedWith: string[];
  sharedAt: string;
  daysAgo: string;
  allApiItems?: SharedFeedbackItem[] | DeveloperFeedbackItem[];
  lastUpdated?: string;
  isDeveloperFeedback?: boolean;
  isRecentlyShared?: boolean;
  hasBeenShared?: boolean;
  preloadedStatuses?: Record<string, "acknowledged" | "unclear" | "reshare">;
  preloadedDevelopers?: Developer[];
  isReshared?: boolean;
  workspaceOwnerName?: string; // ✅ NEW
  workspaceOwnerProfile?: string | null; // ✅ NEW
}

interface ShareFeedbackResponse {
  success: boolean;
  data?: {
    id: number;
    name: string;
    workspace_id: number;
    card_id: number;
    created_by: number;
    url: string;
    screenshot_url: string | null;
    recording_url: string | null;
    voice_recording_url: string | null;
    message: string | null;
    status: "sent" | "viewed" | "in_progress";
    created_at: string;
    shared_at?: string;
    sent_at?: string;
    shared_with?: string[];
  };
  error?: string;
}

interface OnboardingStateData {
  workspaceId?: number;
  workspaceName?: string;
  subdomain?: string;
  fromOnboarding?: boolean;
  workspace?: {
    id: number;
    name: string;
    subdomain: string;
  };
  onboardingMessage?: string;
  userId?: number;
  fromShare?: boolean;
  sharedFeedbackName?: string;
  sharedEmails?: string[];
  showSidebar?: boolean;
  refreshCards?: boolean;
  developerAccess?: boolean;
  viewCardId?: number;
  activeTab?: "all" | "created" | "draft" | "shared";
  preserveData?: boolean;
}

interface HomeScreenProps {
  onHideSidebar?: (hide: boolean) => void;
  workspaceId?: number;
  resetToHome?: boolean;
  pendingNotificationEvent?: {
    type: "shared" | "created";
    data: any;
  } | null;
  onNotificationEventProcessed?: () => void;
}
const HomeScreen: React.FC<HomeScreenProps> = ({
  onHideSidebar,
  workspaceId: propWorkspaceId,
  resetToHome = false,
  pendingNotificationEvent,
  onNotificationEventProcessed,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // State management
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessageText, setSuccessMessageText] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isUploadPopupOpen, setIsUploadPopupOpen] = useState(false);

  const [currentApiResponse, setCurrentApiResponse] =
    useState<CreateCardApiResponse | null>(null);
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [showViewFeedback, setShowViewFeedback] = useState(false);
  const [isOpenedFromDraftCard, setIsOpenedFromDraftCard] = useState(false);
  const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([]);
  const [workspaceState, setWorkspaceState] = useState<WorkspaceState | null>(
    null,
  );
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<number | null>(
    null,
  );
  const [initializationError, setInitializationError] = useState<string | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState<"all" | "created" | "draft" | "shared">("all");
  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);
  const [currentShareCard, setCurrentShareCard] =
    useState<EnhancedDraftCardData | null>(null);
  const [draftCards, setDraftCards] = useState<EnhancedDraftCardData[]>([]);
  const [sharedFeedbackCards, setSharedFeedbackCards] = useState<
    EnhancedSharedFeedbackCard[]
  >([]);
  const [developerCards, setDeveloperCards] = useState<
    EnhancedSharedFeedbackCard[]
  >([]);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);
  const [isLoadingShared, setIsLoadingShared] = useState(false);
  const [isLoadingDeveloper, setIsLoadingDeveloper] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [currentUserName, setCurrentUserName] = useState<string | null>(null);

  // ✅ NEW: Track view-only mode
  const [isViewOnlyMode, setIsViewOnlyMode] = useState(false);
  const [useSharedFeedbackView, setUseSharedFeedbackView] = useState(false);
  const [viewedCards, setViewedCards] = useState<Set<number>>(new Set());

  // Animation states for Mobile and Figma cards
  const [showMobileAnimation, setShowMobileAnimation] = useState(false);
  const [showFigmaAnimation, setShowFigmaAnimation] = useState(false);
  const [mobileAnimationKey, setMobileAnimationKey] = useState(0);
  const [figmaAnimationKey, setFigmaAnimationKey] = useState(0);

  // ✅ NEW: Track if initial data has been loaded
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [shouldRefreshData, setShouldRefreshData] = useState(false);
  const [showBetaPopup, setShowBetaPopup] = useState(false);

  const [showUploadWorkspace, setShowUploadWorkspace] = useState(false);
  const [uploadFeedbackData, setUploadFeedbackData] =
    useState<FeedbackData | null>(null);
  const [uploadApiResponse, setUploadApiResponse] =
    useState<CreateCardApiResponse | null>(null);

  // Picture-in-Picture state
  const [isPiPMode, setIsPiPMode] = useState(false);
  const [pipWorkspaceData, setPipWorkspaceData] = useState<{
    cardDbId: number;
    workspaceId: number;
    feedbackName: string;
    previewImage: string;
    workspaceState: WorkspaceState | null;
    feedbackItems: FeedbackItem[];
  } | null>(null);

  // Add state for tracking upload refreshes
  const [cardsNeedingRefresh, setCardsNeedingRefresh] = useState<Set<number>>(
    new Set(),
  );

  const isRefreshingRef = useRef(false);

  const prevResetToHomeRef = useRef(resetToHome);

  const { profileImage } = useProfileImage();

  const getTimeBasedGreeting = (): string => {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) {
      // return "Good morning";
      return "Hello";
    } else if (hour >= 12 && hour < 17) {
      // return "Good afternoon";
      return "Hello";
    } else if (hour >= 17 && hour < 21) {
      // return "Good evening";
      return "Hello";
    } else {
      // return "Good night";
      return "Hello";
    }
  };

  useEffect(() => {
    // Only trigger reset if resetToHome changed from false to true
    if (resetToHome && !prevResetToHomeRef.current) {
      console.log("🏠 Resetting to home view - full state cleanup");

      // ✅ CRITICAL: Close ALL possible views in correct order
      setShowViewFeedback(false); // Close ViewListFeedback first
      setShowWorkspace(false); // Then close FeedbackWorkspace
      setShowUploadWorkspace(false); // Close upload workspace
      setIsPiPMode(false); // Close PiP mode

      // ✅ Clear ALL temporary data
      setCurrentApiResponse(null);
      setFeedbackItems([]);
      setWorkspaceState(null);
      setUploadFeedbackData(null);
      setUploadApiResponse(null);
      setPipWorkspaceData(null);

      // ✅ Reset ALL mode flags
      setIsViewOnlyMode(false);
      setUseSharedFeedbackView(false);

      // ✅ Show sidebar
      onHideSidebar?.(false);

      console.log(
        "✅ Full reset complete - all views closed, staying on current tab",
      );
    }

    // Update the ref for next comparison
    prevResetToHomeRef.current = resetToHome;
  }, [resetToHome, onHideSidebar]);

  const getDisplayName = (): string => {
    // if (isDeveloperMode && developerEmail) {
    //   const displayName = developerEmail.split("@")[0];
    //   console.log("Returning developer name:", displayName);
    //   return displayName;
    // }

    if (currentUserName) {
      console.log("Returning user full name:", currentUserName);
      return currentUserName;
    }

    console.log("Returning fallback: 'there'");
    return "there";
  };

  /**
   * Marks all notifications for a specific card as read
   * This should be called when a card is opened directly (not from notification)
   */
  const markCardNotificationsAsRead = async (cardId: number) => {
    try {
      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        console.warn("❌ No access token for marking notifications as read");
        return;
      }

      console.log(
        `📥 Fetching notifications for card ${cardId} to mark as read...`,
      );

      // Step 1: Fetch all unread notifications
      const fetchResponse = await fetch(API_URLS.GET_NOTIFICATIONS, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!fetchResponse.ok) {
        console.error(
          "❌ Failed to fetch notifications:",
          fetchResponse.status,
        );
        return;
      }

      const data = await fetchResponse.json();

      if (data.success && data.notifications) {
        // Step 2: Filter notifications related to this specific card
        const cardNotifications = data.notifications.filter(
          (notif: any) => notif.card_id === cardId && !notif.is_read,
        );

        if (cardNotifications.length === 0) {
          console.log(`ℹ️ No unread notifications found for card ${cardId}`);
          return;
        }

        console.log(
          `🔔 Found ${cardNotifications.length} unread notifications for card ${cardId}`,
        );

        // Step 3: Mark each notification as read
        const markAsReadPromises = cardNotifications.map(async (notif: any) => {
          try {
            const response = await fetch(
              API_URLS.MARK_NOTIFICATIONS_AS_READ(notif.id),
              {
                method: "PUT",
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                  "Content-Type": "application/json",
                },
              },
            );

            if (response.ok) {
              console.log(`✅ Marked notification ${notif.id} as read`);
              return true;
            } else {
              console.warn(
                `⚠️ Failed to mark notification ${notif.id} as read`,
              );
              return false;
            }
          } catch (error) {
            console.error(
              `❌ Error marking notification ${notif.id} as read:`,
              error,
            );
            return false;
          }
        });

        // Wait for all mark-as-read operations to complete
        const results = await Promise.all(markAsReadPromises);
        const successCount = results.filter(Boolean).length;

        console.log(
          `✅ Marked ${successCount}/${cardNotifications.length} notifications as read for card ${cardId}`,
        );

        // Step 4: Dispatch event to update notification count in UI
        window.dispatchEvent(
          new CustomEvent("notifications-marked-read", {
            detail: {
              cardId,
              count: successCount,
            },
          }),
        );
      }
    } catch (error) {
      console.error(
        `❌ Error in markCardNotificationsAsRead for card ${cardId}:`,
        error,
      );
    }
  };

  useEffect(() => {
    console.log(
      "👀 Setting up comprehensive upload queue monitor for HomeScreen",
    );

    const checkUploadStatus = () => {
      if (!currentWorkspaceId) return;

      const queue = UploadQueueManager.getQueue();

      // Group completed uploads by card
      const completedByCard = new Map<number, UploadQueueItem[]>();

      queue.forEach((item) => {
        if (item.status === "completed") {
          const items = completedByCard.get(item.cardId) || [];
          items.push(item);
          completedByCard.set(item.cardId, items);
        }
      });

      // Process each card that has completed uploads
      completedByCard.forEach((items, cardId) => {
        console.log(`✅ Card ${cardId} has ${items.length} completed uploads`);

        // Mark card as needing refresh
        setCardsNeedingRefresh((prev) => {
          const newSet = new Set(prev);
          newSet.add(cardId);
          return newSet;
        });

        // Clean up completed items for this card after marking for refresh
        setTimeout(() => {
          items.forEach((item) => {
            UploadQueueManager.removeItem(item.id);
          });
          console.log(
            `🧹 Cleaned up ${items.length} completed items for card ${cardId}`,
          );
        }, 1000);
      });
    };

    // Initial check on mount
    checkUploadStatus();

    // Listen for upload completion events
    const handleUploadCompleted = (event: Event) => {
      console.log("📥 HomeScreen: Upload completed event received");
      checkUploadStatus();
    };

    const handleQueueUpdate = () => {
      checkUploadStatus();
    };

    window.addEventListener("upload-item-completed", handleUploadCompleted);
    window.addEventListener("upload-queue-updated", handleQueueUpdate);

    // Poll every 2 seconds as fallback
    const interval = setInterval(checkUploadStatus, 2000);

    return () => {
      window.removeEventListener(
        "upload-item-completed",
        handleUploadCompleted,
      );
      window.removeEventListener("upload-queue-updated", handleQueueUpdate);
      clearInterval(interval);
    };
  }, [currentWorkspaceId]);

  // Sync activeTab FROM navigation state when sub-pages navigate back (e.g. WorkspacePage → /dashboard)
  useEffect(() => {
    const state = location.state as { activeTab?: string } | null;
    if (!state?.activeTab) return;
    const tab = state.activeTab;
    const stateTab: "all" | "created" | "draft" | "shared" =
      tab === "draft" || tab === "shared" || tab === "created" ? tab : "all";
    if (stateTab !== activeTab) {
      setActiveTab(stateTab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Refresh specific tab data when returning from a save operation, or activate PiP
  useEffect(() => {
    const state = location.state as { refreshTab?: string; pipActive?: boolean } | null;

    // Restore PiP when WorkspacePage minimizes to home
    if (state?.pipActive) {
      window.history.replaceState({}, "", location.pathname + location.search);
      const pipData = pipStore.get();
      if (pipData) {
        setPipWorkspaceData(pipData);
        setIsPiPMode(true);
      }
      return;
    }

    const refreshTab = state?.refreshTab;
    if (!refreshTab) return;

    // Clear state immediately so this doesn't re-trigger on re-renders
    window.history.replaceState({}, "", location.pathname + location.search);

    if (refreshTab === "draft" && currentWorkspaceId) {
      setIsLoadingDrafts(true);
      fetchDraftCards(currentWorkspaceId).then((result) => {
        if (result.success && "data" in result && result.data) {
          setDraftCards(result.data);
        }
      }).finally(() => setIsLoadingDrafts(false));
    } else if (refreshTab === "all" && currentWorkspaceId) {
      setIsLoadingShared(true);
      fetchSharedFeedbackCards(currentWorkspaceId).then((result) => {
        if (result.success && "data" in result && result.data) {
          setSharedFeedbackCards(result.data);
        }
      }).finally(() => setIsLoadingShared(false));
    } else if (refreshTab === "shared" && currentUserEmail) {
      fetchDeveloperFeedbackCardsWithCredentials(currentUserEmail).then((result) => {
        if (result.success && result.data) {
          setDeveloperCards(result.data);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Tabs are in-memory only — no URL sync needed

  useEffect(() => {
    if (cardsNeedingRefresh.size > 0 && currentWorkspaceId) {
      console.log(
        "♻️ Refreshing cards with completed uploads:",
        Array.from(cardsNeedingRefresh),
      );

      // Refresh draft cards
      if (activeTab === "draft") {
        setIsLoadingDrafts(true);
        fetchDraftCards(currentWorkspaceId).then((result) => {
          if (result.success && "data" in result && result.data) {
            setDraftCards(result.data);
            console.log("✅ Draft cards refreshed after upload completion");
          } else if (!result.success) {
            console.error("❌ Failed to refresh draft cards:", result.error);
          }
          setIsLoadingDrafts(false);
        });
      }

      // Clear the refresh set
      setCardsNeedingRefresh(new Set());
    }
  }, [cardsNeedingRefresh, currentWorkspaceId, activeTab]);

  /**
   * Shimmer Component for skeleton loading effect
   */
  const Shimmer = ({ className }: { className: string }) => (
    <div
      className={`animate-pulse ${className}`}
      style={{
        background:
          "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 2s ease-in-out infinite",
      }}
    />
  );

  /**
   * Skeleton loader for the entire home page
   */
  const HomeScreenSkeleton = () => (
    <div className="h-screen bg-white overflow-hidden flex flex-col">
      <style>
        {`
        @keyframes shimmer {
          100%{ background-position: -200% 0; }
         0% { background-position: 200% 0; }
        }
      `}
      </style>

      {/* Header Skeleton */}
      <header className="px-6 py-4 bg-white flex-shrink-0 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Shimmer className="h-6 w-32 rounded" />
          </div>
          <div className="flex items-center space-x-4">
            <Shimmer className="h-10 w-10 rounded-full" />
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto custom-scrollbar-overlay">
        {/* Create New Feedback Section */}
        <div className="px-6 relative pt-6">
          <Shimmer className="h-8 w-48 rounded mb-6" />

          {/* Buttons Grid Skeleton */}
          <div className="relative flex justify-between items-stretch max-w-4xl gap-4 mb-12">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex-1">
                <Shimmer className="h-[100px] w-full rounded-[12px]" />
              </div>
            ))}
          </div>
        </div>

        {/* Cards Grid Skeleton */}
        <div className="px-6 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex flex-col space-y-3">
                <Shimmer className="h-[300px] w-full rounded-lg" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  /**
   * Shimmer skeleton for card grid loading
   */
  const CardGridSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex flex-col space-y-3">
          <div
            className="h-[300px] w-full rounded-lg bg-gray-200 animate-pulse"
            style={{
              background:
                "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 2s ease-in-out infinite",
            }}
          />
          {/*  */}
        </div>
      ))}
    </div>
  );

  // Helper functions
  const calculateDaysAgo = (dateString: string): string => {
    if (!dateString) {
      console.error("Empty date string provided");
      return "Unknown time";
    }

    let date: Date;

    // ✅ FIX: Always treat timestamps from API as UTC
    if (dateString.includes("T")) {
      // If timestamp doesn't have timezone info, assume it's UTC
      if (
        !dateString.includes("Z") &&
        !dateString.includes("+") &&
        !dateString.includes("-", 10)
      ) {
        date = new Date(dateString + "Z");
        console.log("Treating timestamp as UTC:", {
          original: dateString,
          processed: date.toISOString(),
        });
      } else {
        date = new Date(dateString);
      }
    } else {
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) {
      console.error("Invalid date string:", dateString);
      return "Unknown time";
    }

    // ✅ FIX: Use current UTC time for comparison
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffSeconds = Math.floor(diffTime / 1000);
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    console.log("⏱️ Time calculation:", {
      inputTimestamp: dateString,
      parsedDate: date.toISOString(),
      currentTime: now.toISOString(),
      diffSeconds,
      diffMinutes,
      diffHours,
      diffDays,
    });

    if (diffSeconds < 30) return "Just now";
    if (diffSeconds < 60) return "Just now";
    if (diffMinutes === 1) return "1 minute ago";
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    if (diffHours === 1) return "1 hour ago";
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return "1 day ago";
    return `${diffDays} days ago`;
  };

  const getCurrentISTTimestamp = (): string => {
    // ✅ FIX: Return proper UTC timestamp in ISO 8601 format
    return new Date().toISOString();
  };

  const fetchDevelopersForAllCards = useCallback(
    async (
      cardIds: number[],
      token: string,
    ): Promise<Record<number, Developer[]>> => {
      if (cardIds.length === 0) return {};

      console.log(
        `👥 Pre-fetching developers for ${cardIds.length} cards in parallel`,
      );

      try {
        const results = await Promise.all(
          cardIds.map(async (cardId) => {
            try {
              const response = await fetch(
                API_URLS.GET_DEVELOPER_CARD_SENDERS_LIST(cardId),
                {
                  method: "GET",
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                },
              );

              if (!response.ok) {
                console.warn(`Failed to fetch developers for card ${cardId}`);
                return { cardId, developers: [] };
              }

              const data = await response.json();
              return {
                cardId,
                developers:
                  data.success && data.developers ? data.developers : [],
              };
            } catch (error) {
              console.error(
                `Error fetching developers for card ${cardId}:`,
                error,
              );
              return { cardId, developers: [] };
            }
          }),
        );

        const developersMap: Record<number, Developer[]> = {};
        results.forEach(({ cardId, developers }) => {
          developersMap[cardId] = developers;
        });

        console.log(
          `✅ Pre-fetched developers for ${
            Object.keys(developersMap).length
          } cards`,
        );
        return developersMap;
      } catch (error) {
        console.error("Error in fetchDevelopersForAllCards:", error);
        return {};
      }
    },
    [],
  );

  const fetchDevelopersAndOwnerForAllCards = useCallback(
    async (
      cardIds: number[],
      token: string,
    ): Promise<{
      developersMap: Record<number, Developer[]>;
      ownersMap: Record<
        number,
        { ownerName: string; ownerProfile: string | null }
      >;
    }> => {
      if (cardIds.length === 0) return { developersMap: {}, ownersMap: {} };

      console.log(
        `👥 Pre-fetching developers AND owners for ${cardIds.length} cards in parallel`,
      );

      try {
        const results = await Promise.all(
          cardIds.map(async (cardId) => {
            try {
              const response = await fetch(
                API_URLS.GET_DEVELOPER_CARD_SENDERS_LIST(cardId),
                {
                  method: "GET",
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                },
              );

              if (!response.ok) {
                console.warn(`Failed to fetch data for card ${cardId}`);
                return {
                  cardId,
                  developers: [],
                  ownerName: "",
                  ownerProfile: null,
                };
              }

              const data = await response.json();
              return {
                cardId,
                developers:
                  data.success && data.developers ? data.developers : [],
                ownerName: data.workspace_owner_name || "",
                ownerProfile: data.workspace_owner_profile || null,
              };
            } catch (error) {
              console.error(`Error fetching data for card ${cardId}:`, error);
              return {
                cardId,
                developers: [],
                ownerName: "",
                ownerProfile: null,
              };
            }
          }),
        );

        const developersMap: Record<number, Developer[]> = {};
        const ownersMap: Record<
          number,
          { ownerName: string; ownerProfile: string | null }
        > = {};

        results.forEach(({ cardId, developers, ownerName, ownerProfile }) => {
          developersMap[cardId] = developers;
          ownersMap[cardId] = { ownerName, ownerProfile };
        });

        console.log(
          `✅ Pre-fetched data for ${
            Object.keys(developersMap).length
          } cards (developers + owners)`,
        );
        return { developersMap, ownersMap };
      } catch (error) {
        console.error("Error in fetchDevelopersAndOwnerForAllCards:", error);
        return { developersMap: {}, ownersMap: {} };
      }
    },
    [],
  );

  const generatePlaceholderImage = useCallback((feedbackName: string): string => {
    return `https://via.placeholder.com/400x300/6B87E6/FFFFFF?text=${encodeURIComponent(
      feedbackName.substring(0, 20) + (feedbackName.length > 20 ? "..." : ""),
    )}`;
  }, []);

  const memoizedImageUrls = useMemo(() => new Map<string, string>(), []);

  const getValidImageUrl = useCallback(
    (imagePath: string | null | undefined): string | undefined => {
      if (!imagePath) return undefined;

      // Check memo cache first
      if (memoizedImageUrls.has(imagePath)) {
        return memoizedImageUrls.get(imagePath);
      }

      let result: string | undefined;

      if (imagePath.startsWith("data:image/")) {
        result = imagePath;
      } else if (imagePath.startsWith("http")) {
        result = imagePath;
      } else if (imagePath.startsWith("/")) {
        result = `${BASE_URL}${imagePath}`;
      } else {
        result = `${BASE_URL}/${imagePath}`;
      }

      // Cache the result
      if (result) {
        memoizedImageUrls.set(imagePath, result);
      }

      return result;
    },
    [memoizedImageUrls],
  );
  useEffect(() => {
    if (currentUserEmail && !initialDataLoaded) {
      console.log("🚀 Prefetching developer token...");
      DeveloperTokenManager.prefetchToken(currentUserEmail);
    }
  }, [currentUserEmail, initialDataLoaded]);

  const fetchStatusesForCard = async (
    cardId: number,
  ): Promise<Record<string, "acknowledged" | "unclear" | "reshare">> => {
    const accessToken = EnhancedTokenManager.getAccessToken();
    if (!accessToken) {
      console.warn("❌ No access token for status fetch");
      return {};
    }

    const statusMap: Record<string, "acknowledged" | "unclear" | "reshare"> =
      {};

    console.log(`🚀 Fetching all statuses for card ${cardId} in one API call`);

    try {
      const response = await fetch(
        API_URLS.GET_FULL_CARDS_FEEDBACK_STATUS_UPDATE(cardId),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        console.error(
          `❌ Status fetch failed for card ${cardId}:`,
          response.status,
        );
        return {};
      }

      const data = await response.json();
      console.log(`📊 Full card status response for card ${cardId}:`, data);

      // Parse the response structure
      if (data.feedbacks && Array.isArray(data.feedbacks)) {
        data.feedbacks.forEach((feedback: any) => {
          const feedbackId = feedback.feedback_id?.toString();

          if (!feedbackId) return;

          // Check if this feedback has any developer statuses
          if (feedback.developers && Array.isArray(feedback.developers)) {
            // Find the latest status (developers array should be sorted by action_time)
            const latestDeveloper = feedback.developers[0]; // Assuming sorted by latest first

            if (latestDeveloper?.status) {
              const status = latestDeveloper.status.toLowerCase();

              if (
                status === "acknowledged" ||
                status === "unclear" ||
                "reshare"
              ) {
                statusMap[feedbackId] = status as
                  | "acknowledged"
                  | "unclear"
                  | "reshare";

                console.log(`✅ Status found for feedback ${feedbackId}:`, {
                  status: status,
                  developer: latestDeveloper.developer_email,
                  actionTime: latestDeveloper.action_time,
                  unclearReason: latestDeveloper.unclear_reason,
                  comment: latestDeveloper.comment,
                });
              }
            }
          }
        });
      }

      console.log(
        `✅ Extracted ${
          Object.keys(statusMap).length
        } statuses from card ${cardId}`,
      );
      return statusMap;
    } catch (error) {
      console.error(`❌ Error fetching card statuses for ${cardId}:`, error);
      return {};
    }
  };

  // Check for developer access in URL parameters
  //   useEffect(() => {
  //   const urlParams = new URLSearchParams(location.search);
  //   const token = urlParams.get("token");
  //   const email = urlParams.get("developer");
  //   const cardIdParam = urlParams.get("cardId");

  //   if (token && email) {
  //     if (token.length >= 10 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(decodeURIComponent(email))) {
  //       const decodedEmail = decodeURIComponent(email);
  //       const parsedCardId = cardIdParam ? parseInt(cardIdParam) : undefined;

  //       console.log("🔐 Developer credentials detected - storing token");

  //       // Store in DeveloperTokenManager only (single source of truth)
  //       DeveloperTokenManager.storeToken(token, decodedEmail, parsedCardId);
  //       setActiveTab("shared");

  //       const newUrl = location.pathname;
  //       navigate(newUrl, { replace: true });

  //       console.log("✅ Developer token stored successfully");
  //     } else {
  //       console.error("❌ Invalid developer credentials in URL");
  //       setInitializationError(
  //         "Invalid access link. Please check your email for the correct link."
  //       );
  //     }
  //   } else {
  //     // Check for stored developer session
  //     console.log("🔍 Checking for stored developer session...");
  //     const storedEmail = localStorage.getItem('last_developer_email');

  //     if (storedEmail) {
  //       const storedToken = DeveloperTokenManager.getTokenForEmail(storedEmail);

  //       if (storedToken && DeveloperTokenManager.isTokenValid(storedEmail, storedToken)) {
  //         console.log("✅ Restoring previous developer session");
  //         setActiveTab("shared");
  //       } else {
  //         console.log("⏰ Stored token expired, cleaning up");
  //         DeveloperTokenManager.removeToken(storedEmail, storedToken!);
  //         localStorage.removeItem('last_developer_email');
  //       }
  //     }
  //   }

  //   setIsInitializing(false);
  // }, [location.search, navigate]);

  // Regular user initialization - always run regardless of developer credentials
  useEffect(() => {
    // ✅ CRITICAL FIX: Only initialize once when currentWorkspaceId is null
    if (currentWorkspaceId !== null) {
      console.log(
        "⏭️ Skipping initialization - workspace already loaded:",
        currentWorkspaceId,
      );
      return;
    }

    console.log("HomeScreen initialization started");

    const initializeUserSession = async () => {
      const navigationState = location.state as OnboardingStateData | null;

      try {
        setIsInitializing(true);

        // ✅ NEW: Check for tokens in URL first (from subdomain navigation)
        const tokensRestored = await SubdomainManager.restoreTokensFromUrl();
        if (tokensRestored) {
          console.log("✅ Tokens restored from URL after subdomain navigation");
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Regular user initialization flow
        if (shouldRedirectToOnboarding()) {
          console.log("User needs onboarding, redirecting...");
          navigate("/onboarding", { replace: true });
          setIsInitializing(false);
          return;
        }

        // Fetch user profile to get name and email
        const profileResult = await fetchUserProfileAndWorkspace();
        if (profileResult.success && profileResult.data) {
          console.log("Profile data received:", profileResult.data);

          // Store in BOTH localStorage AND window for immediate access
          localStorage.setItem("current_user_email", profileResult.data.email);
          (window as any).__current_user_email = profileResult.data.email;

          setCurrentUserEmail(profileResult.data.email);
          setCurrentUserName(profileResult.data.full_name);

          // ✅ NEW: Initialize developer token for shared tab access
          const accessToken = EnhancedTokenManager.getAccessToken();
          if (accessToken && profileResult.data.email) {
            const tokenKey = `developer_token_${profileResult.data.email}`;
            const expiryKey = `developer_token_expiry_${profileResult.data.email}`;

            // Set expiry to 24 hours from now
            const expiry = Date.now() + 24 * 60 * 60 * 1000;

            localStorage.setItem(tokenKey, accessToken);
            localStorage.setItem(expiryKey, expiry.toString());

            console.log(
              "✅ Developer token initialized for shared tab:",
              profileResult.data.email,
            );
          }

          if (!profileResult.data.onboarded) {
            console.log("User profile indicates not onboarded, redirecting...");
            navigate("/onboarding", { replace: true });
            setIsInitializing(false);
            return;
          }
        } else {
          // Only check authentication if profile fetch fails
          if (!canAccessDashboard()) {
            console.log(
              "User cannot access dashboard, checking authentication...",
            );

            const accessToken = EnhancedTokenManager.getAccessToken();
            const refreshToken = EnhancedTokenManager.getRefreshToken();

            if (!accessToken || !refreshToken) {
              console.error("No authentication tokens found");
              setInitializationError(
                "Authentication required. Please log in again.",
              );
              setIsInitializing(false);
              return;
            }

            console.error("Could not fetch user profile:", profileResult.error);
            setInitializationError(
              "Could not verify user status. Please try logging in again.",
            );
            setIsInitializing(false);
            return;
          }
        }

        let workspaceId: number | null = null;

        if (navigationState?.workspaceId) {
          workspaceId = navigationState.workspaceId;
          const currentUserId = EnhancedTokenManager.getCurrentUserId();
          if (currentUserId) {
            const workspaceData = {
              id: workspaceId,
              name: navigationState.workspaceName || "Workspace",
              subdomain: navigationState.subdomain,
            };
            EnhancedTokenManager.setWorkspaceDataForUser(
              currentUserId,
              workspaceData,
            );
          }
        } else if (navigationState?.workspace?.id) {
          const workspaceData = navigationState.workspace;
          workspaceId = workspaceData.id;
          const currentUserId = EnhancedTokenManager.getCurrentUserId();
          if (currentUserId) {
            EnhancedTokenManager.setWorkspaceDataForUser(
              currentUserId,
              workspaceData,
            );
          }
        } else {
          workspaceId = await getWorkspaceIdWithFallback();
        }

        if (!workspaceId || workspaceId <= 0) {
          console.error("No valid workspace found after all attempts");
          debugWorkspaceStatus();
          setInitializationError(
            "No workspace found. Please complete the onboarding process.",
          );
          setIsInitializing(false);
          return;
        }

        console.log(
          "HomeScreen initialization successful, workspace:",
          workspaceId,
        );
        setCurrentWorkspaceId(workspaceId);
        setInitializationError(null);

        if (navigationState?.fromShare) {
          if (navigationState.showSidebar) {
            onHideSidebar?.(false);
          }
        }
      } catch (error) {
        console.error("Error during initialization:", error);
        setInitializationError(
          "Failed to initialize workspace. Please try logging in again.",
        );
      } finally {
        setIsInitializing(false);
        console.log("✅ Initialization complete, loading state set to false");
      }
    };

    initializeUserSession();
  }, [location.state, propWorkspaceId, onHideSidebar, navigate]);

  // Function to fetch developer feedback cards using developer credentials
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchDeveloperFeedbackCardsWithCredentials = useCallback(async (email?: string) => {
    try {
      const emailToUse = email;
      if (!emailToUse) {
        console.error("No developer email available");
        return { success: false, error: "Developer email not found" };
      }

      const developerToken =
        await DeveloperTokenManager.getDeveloperTokenForEmail(emailToUse);
      if (!developerToken) {
        console.error("No developer token found for email:", emailToUse);
        return { success: false, error: "Developer token not found" };
      }

      // ✅ Fetching developer cards with batch creator avatar pre-loading
      console.log(
        "🚀 Fetching developer cards with batch creator avatar pre-loading",
      );

      const headers: HeadersInit = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${developerToken}`,
        "X-Developer-Email": emailToUse,
      };

      const response = await fetch(API_URLS.VIEW_FEEDBACK_BY_DEVELOPER, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Developer feedback API error:", {
          status: response.status,
          body: errorText,
        });

        if (response.status === 401) {
          DeveloperTokenManager.removeToken(emailToUse, developerToken);
          localStorage.removeItem("last_developer_email");
        }

        return {
          success: false,
          error: `Developer authentication failed. Status: ${response.status}`,
        };
      }

      const responseData: DeveloperFeedbackCard[] = await response.json();
      console.log("✅ API Success:", {
        cardCount: responseData.length,
        cardIds: responseData.map((c) => c.card_id),
      });

      // ✅ Extract all card IDs for creator avatar pre-fetching
      const allCardIds = responseData.map((card) => card.card_id);

      // Pre-fetch CREATORS and OWNERS for ALL cards in parallel
      const accessToken = EnhancedTokenManager.getAccessToken();
      const { developersMap, ownersMap } = accessToken
        ? await fetchDevelopersAndOwnerForAllCards(allCardIds, accessToken)
        : { developersMap: {}, ownersMap: {} };

      console.log(
        "✅ Pre-fetched creators and owners for developer cards:",
        Object.keys(developersMap).length,
      );

      // Transform cards with pre-loaded data
      const developerSharedCards: EnhancedSharedFeedbackCard[] =
        await Promise.all(
          responseData.map(async (card) => {
            const { card_id, card_name, feedbacks } = card;

            const firstScreenshot = feedbacks.find(
              (f) => f.screenshot_url,
            )?.screenshot_url;
            const cardImageUrl = firstScreenshot
              ? getValidImageUrl(firstScreenshot) ||
                generatePlaceholderImage(card_name)
              : generatePlaceholderImage(card_name);

            const actualFeedbackCount = feedbacks.reduce((count, item) => {
              return item.message ||
                item.screenshot_url ||
                item.recording_url ||
                item.voice_recording_url
                ? count + 1
                : count;
            }, 0);

            // ✅ CRITICAL FIX: Find EARLIEST feedback (original share time)
            // Sort by sent_at/created_at ascending to get the first one
            const sortedByTime = [...feedbacks].sort((a, b) => {
              const aTime = new Date(a.sent_at || a.created_at).getTime();
              const bTime = new Date(b.sent_at || b.created_at).getTime();
              return aTime - bTime; // Ascending order - earliest first
            });

            // ✅ Use the FIRST (earliest) feedback for the card's "shared at" time
            const earliestFeedback = sortedByTime[0];
            const originalShareTime =
              earliestFeedback?.sent_at || earliestFeedback?.created_at;
            const daysAgo = calculateDaysAgo(originalShareTime);

            // Check if this is a recent share (within 24 hours of ORIGINAL share)
            const sharedDate = new Date(originalShareTime);
            const now = new Date();
            const hoursSinceShared =
              (now.getTime() - sharedDate.getTime()) / (1000 * 60 * 60);
            const isRecentlyShared = hoursSinceShared <= 24;

            console.log(`📅 Card ${card_id} - Using ORIGINAL share time:`, {
              cardName: card_name,
              originalShareTime: originalShareTime,
              daysAgo: daysAgo,
              totalFeedbacks: feedbacks.length,
              isRecentlyShared: isRecentlyShared,
            });

            // ✅ Fetch statuses
            const statusMap = await fetchStatusesForCard(card_id);

            // Get pre-loaded creators from map
            const creators = developersMap[card_id] || [];
            const ownerData = ownersMap[card_id] || {
              ownerName: "",
              ownerProfile: null,
            };

            // ✅ ADD THIS BLOCK - Check if card has reshared feedback
            const reshareMarker = localStorage.getItem(
              `card_${card_id}_reshared`,
            );
            const isReshared = reshareMarker === "true";

            console.log(`📋 Card ${card_id} reshare status:`, {
              cardName: card_name,
              reshareMarker,
              isReshared,
            });

            return {
              id: card_id,
              name: card_name,
              created_at:
                earliestFeedback?.created_at || new Date().toISOString(),
              imageUrl: cardImageUrl,
              feedbackCount: actualFeedbackCount,
              status: "Shared with me" as const,
              sharedWith: [],
              sharedAt: originalShareTime,
              daysAgo: daysAgo,
              allApiItems: feedbacks,
              lastUpdated: new Date().toISOString(),
              isDeveloperFeedback: true,
              isRecentlyShared: isRecentlyShared,
              preloadedStatuses: statusMap,
              preloadedDevelopers: creators,
              workspaceOwnerName: ownerData.ownerName,
              workspaceOwnerProfile: ownerData.ownerProfile,
              isReshared: isReshared,
            };
          }),
        );

      localStorage.setItem("last_developer_email", emailToUse);

      // ✅ IMPORTANT: Sort cards by ORIGINAL share time (newest original share first)
      const sortedCards = developerSharedCards.sort(
        (a, b) =>
          new Date(b.sharedAt).getTime() - new Date(a.sharedAt).getTime(),
      );

      console.log("✅ Cards sorted by original share time (newest first)");

      // ✅ Notify DashboardScreen that cards have been processed
      sortedCards.forEach((card) => {
        window.dispatchEvent(
          new CustomEvent("card-processed-notification", {
            detail: { cardId: card.id },
          }),
        );
      });

      console.log(
        `✅ Notified DashboardScreen of ${sortedCards.length} processed cards`,
      );

      return {
        success: true,
        data: sortedCards,
      };
    } catch (error) {
      console.error("❌ Error fetching developer cards:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Network error occurred",
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Enhanced draft cards fetch function that works with developer credentials
  const fetchDraftCardsWithCredentials = async (
    token: string,
    workspaceId?: number,
  ) => {
    try {
      console.log("Fetching draft cards with credentials");

      // ✅ CHANGE: Use API_URLS.GET_DRAFT instead of hardcoded URL
      const url = workspaceId
        ? API_URLS.GET_DRAFT(workspaceId)
        : API_URLS.GET_DRAFT(0); // Fallback with dummy ID if no workspace

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          error: `HTTP error! status: ${response.status}, message: ${errorText}`,
        };
      }

      const responseData: DraftApiResponse = await response.json();
      console.log("Draft API Response:", responseData);

      // Transform drafts (same logic as before)
      const transformedDrafts: EnhancedDraftCardData[] = await Promise.all(
        Object.entries(responseData).map(async ([cardName, cardData]) => {
          const { card_id, card_name, preview_screenshot, feedbacks } =
            cardData;

          const cardImageUrl = preview_screenshot
            ? getValidImageUrl(preview_screenshot) ||
              generatePlaceholderImage(card_name)
            : generatePlaceholderImage(card_name);

          const actualFeedbackCount = feedbacks.filter(
            (item) =>
              item.message ||
              item.screenshot_url ||
              item.recording_url ||
              item.voice_recording_url,
          ).length;

          const sortedFeedbacks = feedbacks.sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          );

          // Transform feedback items and create workspace state
          const transformedFeedbackItems: FeedbackItem[] = await Promise.all(
            feedbacks
              .filter(
                (item) =>
                  item.message ||
                  item.screenshot_url ||
                  item.recording_url ||
                  item.voice_recording_url,
              )
              .sort(
                (a, b) =>
                  new Date(a.created_at).getTime() -
                  new Date(b.created_at).getTime(),
              )
              .map(async (item, index) => {
                const screenshotUrl = getValidImageUrl(item.screenshot_url);
                const recordingUrl = getValidImageUrl(item.recording_url);
                const audioUrl = getValidImageUrl(item.voice_recording_url);
                const stableId = item.id.toString();

                let itemType: "screenshot" | "recording" = "screenshot";
                if (recordingUrl && !screenshotUrl) {
                  itemType = "recording";
                } else if (recordingUrl && screenshotUrl) {
                  itemType = "recording";
                }

                const feedbackItem: FeedbackItem = {
                  id: stableId,
                  type: itemType,
                  timestamp: new Date(item.created_at).getTime(),
                  order: index + 1,
                  feedback: item.message || "",
                  hasAudio: !!audioUrl,
                  audioUrl: audioUrl, // ✅ Store API URL directly - no blob needed
                  audioFilename: audioUrl ? `audio_${item.id}.wav` : undefined,
                  user: {
                    name: "User",
                    avatar: AvatarIcon,
                  },
                  isSelected: false,
                  apiFeedbackId: item.id, // ✅ CRITICAL: Preserve this from API response
                  uniqueFeedbackId: stableId, // ✅ This is the same value for consistency
                };

                if (itemType === "screenshot") {
                  feedbackItem.annotatedImage = screenshotUrl;
                  feedbackItem.originalImage = screenshotUrl;
                } else if (itemType === "recording") {
                  feedbackItem.dataUrl = recordingUrl;
                  feedbackItem.duration = 30;

                  // Load video thumbnail
                  try {
                    const thumbnailUrl = await getVideoThumbnail(item.id);
                    if (thumbnailUrl) {
                      feedbackItem.thumbnailUrl = thumbnailUrl;
                      console.log(
                        `✅ Thumbnail loaded for recording ${stableId}:`,
                        thumbnailUrl,
                      );
                    }
                  } catch (error) {
                    console.warn(
                      `⚠️ Failed to load thumbnail for recording ${stableId}:`,
                      error,
                    );
                  }

                  // Load recording blob
                  if (recordingUrl) {
                    try {
                      const blobResponse = await fetch(recordingUrl);
                      const blob = await blobResponse.blob();
                      feedbackItem.recordingBlob = blob;
                    } catch (error) {
                      console.warn(
                        `Failed to load recording blob for ${stableId}:`,
                        error,
                      );
                      feedbackItem.recordingBlob = new Blob([], {
                        type: "video/mp4",
                      });
                    }
                  } else {
                    feedbackItem.recordingBlob = new Blob([], {
                      type: "video/mp4",
                    });
                  }
                }

                return feedbackItem;
              }),
          );

          console.log("📋 HomeScreen - Transformed feedback items order:", {
            cardName: card_name,
            totalItems: transformedFeedbackItems.length,
            itemSequence: transformedFeedbackItems.map((item, idx) => ({
              position: idx + 1,
              type: item.type,
              id: item.id,
              apiFeedbackId: item.apiFeedbackId,
              uniqueFeedbackId: item.uniqueFeedbackId,
              order: item.order,
              timestamp: isNaN(item.timestamp) ? "invalid" : new Date(item.timestamp).toISOString(),
            })),
          });

          console.log("📋 HomeScreen - Transformed feedback items order:", {
            cardName: card_name,
            totalItems: transformedFeedbackItems.length,
            itemSequence: transformedFeedbackItems.map((item, idx) => ({
              position: idx + 1,
              type: item.type,
              id: item.id,
              order: item.order,
              timestamp: isNaN(item.timestamp) ? "invalid" : new Date(item.timestamp).toISOString(),
            })),
          });

          console.log("📋 Loaded draft feedbacks in chronological order:", {
            cardName: card_name,
            totalItems: transformedFeedbackItems.length,
            order: transformedFeedbackItems.map((item, idx) => ({
              position: idx + 1,
              type: item.type,
              timestamp: isNaN(item.timestamp) ? "invalid" : new Date(item.timestamp).toISOString(),
            })),
          });

          const feedbackItemsOrder = transformedFeedbackItems.map(
            (item) => `${item.type}-${item.id}`,
          );

          console.log("✅ Created feedbackItemsOrder:", {
            cardName: card_name,
            orderArray: feedbackItemsOrder,
            totalItems: feedbackItemsOrder.length,
          });

          const screenshotSubmissions: ScreenshotSubmission[] =
            transformedFeedbackItems
              .filter(
                (item) => item.type === "screenshot" && item.annotatedImage,
              )
              .map((item) => ({
                id: item.id,
                annotatedImage: item.annotatedImage!,
                originalImage: item.originalImage || item.annotatedImage!,
                feedback: item.feedback || "",
                timestamp: item.timestamp,
                hasAudio: item.hasAudio || false,
                audioUrl: item.audioUrl, // ✅ API URL - will be fetched directly by VoiceNoteRecorder
                audioFilename: item.audioFilename,
                uniqueFeedbackId: item.uniqueFeedbackId,
                // ✅ Don't store blob - it becomes invalid after page reload
                audioBlob: undefined,
              }));

          const recordingSubmissions: RecordingSubmission[] =
            transformedFeedbackItems
              .filter((item) => item.type === "recording" && item.dataUrl)
              .map((item) => ({
                id: item.id,
                recordingBlob:
                  item.recordingBlob || new Blob([], { type: "video/mp4" }),
                dataUrl: item.dataUrl!,
                feedback: item.feedback || "",
                timestamp: item.timestamp,
                duration: item.duration || 30,
                hasAudio: item.hasAudio || false,
                uniqueFeedbackId: item.uniqueFeedbackId,
                thumbnailUrl: item.thumbnailUrl,
              }));
          const savedWorkspaceState: WorkspaceState = {
            screenshots: screenshotSubmissions,
            recordings: recordingSubmissions,
            feedbackItemsOrder: feedbackItemsOrder,
            url: feedbacks[0]?.url,
            iframeHistory: feedbacks[0]?.url ? [feedbacks[0].url] : [],
            iframeHistoryIndex: 0,
            showIframe: !!feedbacks[0]?.url,
            feedbackId: feedbacks[0]?.id?.toString(),
            isUrlLocked: transformedFeedbackItems.length > 0,
          };

          // Prefer recording thumbnail when first item is a recording (preview_screenshot
          // may be a blank frame captured alongside the recording upload)
          const firstItem = transformedFeedbackItems[0];
          const finalImageUrl =
            firstItem?.type === "recording" && firstItem?.thumbnailUrl
              ? firstItem.thumbnailUrl
              : (preview_screenshot && getValidImageUrl(preview_screenshot))
                ? cardImageUrl
                : cardImageUrl;

          return {
            id: card_id,
            name: card_name,
            created_at:
              sortedFeedbacks[0]?.created_at || new Date().toISOString(),
            imageUrl: finalImageUrl,
            feedbackCount: actualFeedbackCount,
            feedbackData: {
              name: card_name,
              collaborators: [],
              accessType: "comment" as const,
            },
            status: "draft" as const,
            savedFeedbackItems: transformedFeedbackItems,
            savedWorkspaceState: savedWorkspaceState,
            lastUpdated: sortedFeedbacks[0]?.created_at || new Date().toISOString(),
            allApiItems: feedbacks,
          };
        }),
      );

      return {
        success: true,
        data: transformedDrafts.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        ),
      };
    } catch (error) {
      console.error("Error in fetchDraftCardsWithCredentials:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  };

  // Regular draft cards fetch function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchDraftCards = useCallback(async (workspaceId: number) => {
    try {
      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        return { success: false, error: "No access token available" };
      }

      return await fetchDraftCardsWithCredentials(accessToken, workspaceId);
    } catch (error) {
      console.error("Error in fetchDraftCards:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Enhanced shared feedback fetch function
  const fetchSharedFeedbackCardsWithCredentials = async (
    token: string,
    workspaceId?: number,
  ) => {
    try {
      console.log(
        "🚀 Fetching shared feedback cards with batch developer pre-fetch",
      );

      const url = workspaceId
        ? API_URLS.GET_CREATED_FEEDBACK(workspaceId)
        : API_URLS.GET_CREATED_FEEDBACK(0);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          error: `HTTP error! status: ${response.status}, message: ${errorText}`,
        };
      }

      const responseData: SharedApiResponse = await response.json();
      console.log(
        `📊 Fetched ${Object.keys(responseData).length} shared cards`,
      );

      // ✅ Extract all card IDs first
      const allCardIds = Object.entries(responseData)
        .filter(
          ([_, cardData]) =>
            cardData.feedbacks && cardData.feedbacks.length > 0,
        )
        .map(([_, cardData]) => cardData.card_id);

      // Pre-fetch developers and owners for ALL cards in parallel
      const { developersMap, ownersMap } =
        await fetchDevelopersAndOwnerForAllCards(allCardIds, token);
      const transformedShared: EnhancedSharedFeedbackCard[] = await Promise.all(
        Object.entries(responseData)
          .map(async ([cardName, cardData]) => {
            const { card_id, card_name, preview_screenshot, feedbacks } =
              cardData;

            if (!feedbacks || feedbacks.length === 0) {
              return null;
            }

            const cardImageUrl = preview_screenshot
              ? getValidImageUrl(preview_screenshot) ||
                generatePlaceholderImage(card_name)
              : generatePlaceholderImage(card_name);

            const actualFeedbackCount = feedbacks.filter(
              (item) =>
                item.feedback.message ||
                item.feedback.screenshot_url ||
                item.feedback.recording_url ||
                item.feedback.voice_recording_url,
            ).length;

            const sortedFeedbacks = feedbacks.sort(
              (a, b) =>
                new Date(b.feedback.created_at).getTime() -
                new Date(a.feedback.created_at).getTime(),
            );

            const firstFeedback = sortedFeedbacks[0];
            const sharedAt = firstFeedback.sent_at;
            const daysAgo = calculateDaysAgo(sharedAt);

            const STATUS_PRIORITY: Record<string, number> = { done: 4, completed: 4, in_progress: 3, viewed: 2, invited: 1 };
            let topRawStatus = "invited";
            for (const entry of feedbacks) {
              const s = entry.feedback.updated_status;
              if (s && (STATUS_PRIORITY[s] ?? 0) > (STATUS_PRIORITY[topRawStatus] ?? 0)) {
                topRawStatus = s;
              }
            }
            const STATUS_LABEL_MAP: Record<string, "Invited" | "Viewed" | "In progress" | "Done"> = {
              invited: "Invited",
              viewed: "Viewed",
              in_progress: "In progress",
              done: "Done",
              completed: "Done",
            };
            const status = STATUS_LABEL_MAP[topRawStatus] ?? "Invited";

            // ✅ Pre-fetch statuses
            console.log(
              `📥 Pre-fetching statuses for card ${card_id} (${card_name})`,
            );
            const statusMap = await fetchStatusesForCard(card_id);
            console.log(
              `✅ Pre-fetched ${
                Object.keys(statusMap).length
              } statuses for card ${card_id}`,
            );

            // ✅ Get pre-loaded developers from map (already fetched in parallel)
            const developers = developersMap[card_id] || [];
            const ownerData = ownersMap[card_id] || {
              ownerName: "",
              ownerProfile: null,
            };
            console.log(
              `✅ Using pre-loaded developers for card ${card_id}:`,
              developers.length,
            );

            return {
              id: card_id,
              name: card_name,
              created_at: firstFeedback.feedback.created_at,
              imageUrl: cardImageUrl,
              feedbackCount: actualFeedbackCount,
              status: status,
              sharedWith: [],
              sharedAt: sharedAt,
              daysAgo: daysAgo,
              allApiItems: feedbacks,
              lastUpdated: new Date().toISOString(),
              isDeveloperFeedback: false,
              hasBeenShared: true,
              preloadedStatuses: statusMap,
              preloadedDevelopers: developers,
              workspaceOwnerName: ownerData.ownerName,
              workspaceOwnerProfile: ownerData.ownerProfile,
            };
          })
          .filter(Boolean) as Promise<EnhancedSharedFeedbackCard>[],
      );

      console.log(
        `✅ Transformed ${transformedShared.length} shared cards with pre-loaded developers`,
      );

      return {
        success: true,
        data: transformedShared.sort(
          (a, b) =>
            new Date(b.sharedAt).getTime() - new Date(a.sharedAt).getTime(),
        ),
      };
    } catch (error) {
      console.error("Error fetching shared feedback cards:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  };

  // Regular shared feedback fetch function
  const fetchSharedFeedbackCards = async (workspaceId: number) => {
    try {
      const accessToken = EnhancedTokenManager.getAccessToken();
      if (!accessToken) {
        return { success: false, error: "No access token available" };
      }

      return await fetchSharedFeedbackCardsWithCredentials(
        accessToken,
        workspaceId,
      );
    } catch (error) {
      console.error("Error fetching shared feedback cards:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  };

  // ✅ NEW: Memoized fetch function to prevent unnecessary re-renders
  const fetchAllData = useCallback(async () => {
    if (!currentWorkspaceId) {
      console.warn("⚠️ No workspace ID available, skipping fetch");
      return;
    }

    console.log("🚀 Starting PARALLEL data fetch for all tabs...");
    const startTime = performance.now();

    // ✅ DEBUG: Log token state before fetching
    if (currentUserEmail) {
      const tokenKey = `developer_token_${currentUserEmail}`;
      const expiryKey = `developer_token_expiry_${currentUserEmail}`;
      const token = localStorage.getItem(tokenKey);
      const expiry = localStorage.getItem(expiryKey);

      console.log("🔍 Developer token debug:", {
        email: currentUserEmail,
        hasToken: !!token,
        tokenLength: token?.length || 0,
        expiry: expiry ? new Date(parseInt(expiry)).toISOString() : "none",
        isExpired: expiry ? Date.now() > parseInt(expiry) : true,
        currentTime: new Date().toISOString(),
      });
    }

    // ✅ Set ALL loading states BEFORE starting fetches
    setIsLoadingShared(true);
    setIsLoadingDrafts(true);
    setIsLoadingDeveloper(true);

    try {
      // ✅ PARALLEL FETCH: All three API calls start simultaneously
      const [sharedResult, draftResult, developerResult] = await Promise.all([
        // 1️⃣ Shared cards (Created tab) with timing
        (async () => {
          const fetchStart = performance.now();
          const result = await fetchSharedFeedbackCards(
            currentWorkspaceId,
          ).catch((error) => {
            console.error("❌ Shared cards fetch failed:", error);
            return {
              success: false,
              error: error.message || "Unknown error",
              data: [],
            };
          });
          const fetchEnd = performance.now();
          console.log(
            `⏱️ Shared cards fetched in ${(fetchEnd - fetchStart).toFixed(0)}ms`,
            {
              success: result.success,
              // ✅ FIXED: Safe access to data
              count: "data" in result && result.data ? result.data.length : 0,
            },
          );
          return result;
        })(),

        // 2️⃣ Draft cards with timing
        (async () => {
          const fetchStart = performance.now();
          const result = await fetchDraftCards(currentWorkspaceId).catch(
            (error) => {
              console.error("❌ Draft cards fetch failed:", error);
              return {
                success: false,
                error: error.message || "Unknown error",
                data: [],
              };
            },
          );
          const fetchEnd = performance.now();
          console.log(
            `⏱️ Draft cards fetched in ${(fetchEnd - fetchStart).toFixed(0)}ms`,
            {
              success: result.success,
              // ✅ FIXED: Safe access to data
              count: "data" in result && result.data ? result.data.length : 0,
            },
          );
          return result;
        })(),

        // 3️⃣ Developer cards (Shared tab) - FIXED conditional fetch
        (async () => {
          if (!currentUserEmail) {
            console.log("ℹ️ No user email - skipping developer cards");
            return { success: true, data: [] };
          }

          // ✅ FIXED: Use async getDeveloperTokenForEmail instead of manual validation
          try {
            const developerToken =
              await DeveloperTokenManager.getDeveloperTokenForEmail(
                currentUserEmail,
              );

            if (!developerToken) {
              console.log(
                "ℹ️ No developer token available - skipping developer cards",
              );
              return { success: true, data: [] };
            }

            console.log("✅ Developer token exists - fetching shared feedback");
            const fetchStart = performance.now();
            const result = await fetchDeveloperFeedbackCardsWithCredentials(
              currentUserEmail,
            ).catch((error) => {
              console.error("❌ Developer cards fetch failed:", error);
              return {
                success: false,
                error: error.message || "Unknown error",
                data: [],
              };
            });
            const fetchEnd = performance.now();
            console.log(
              `⏱️ Developer cards fetched in ${(fetchEnd - fetchStart).toFixed(0)}ms`,
              {
                success: result.success,
                // ✅ FIXED: Safe access to data
                count: "data" in result && result.data ? result.data.length : 0,
              },
            );
            return result;
          } catch (error) {
            console.error("❌ Error checking developer token:", error);
            return { success: true, data: [] };
          }
        })(),
      ]);

      const endTime = performance.now();
      const totalTime = (endTime - startTime).toFixed(0);
      console.log(`✅ Parallel fetch completed in ${totalTime}ms`);

      // ✅ Log summary of all results
      console.log("📊 Fetch results summary:", {
        shared: {
          success: sharedResult.success,
          count:
            "data" in sharedResult && sharedResult.data
              ? sharedResult.data.length
              : 0,
          error: "error" in sharedResult ? sharedResult.error : null,
        },
        draft: {
          success: draftResult.success,
          count:
            "data" in draftResult && draftResult.data
              ? draftResult.data.length
              : 0,
          error: "error" in draftResult ? draftResult.error : null,
        },
        developer: {
          success: developerResult.success,
          count:
            "data" in developerResult && developerResult.data
              ? developerResult.data.length
              : 0,
          error: "error" in developerResult ? developerResult.error : null,
        },
        totalDuration: `${totalTime}ms`,
      });

      // ✅ FIXED: Update state with type-safe checks
      if (
        sharedResult.success &&
        "data" in sharedResult &&
        Array.isArray(sharedResult.data)
      ) {
        setSharedFeedbackCards(sharedResult.data);
      } else if ("error" in sharedResult && sharedResult.error) {
        console.error(
          "⚠️ Shared cards error (preserving existing data):",
          sharedResult.error,
        );
      }

      if (
        draftResult.success &&
        "data" in draftResult &&
        Array.isArray(draftResult.data)
      ) {
        setDraftCards(draftResult.data);
      } else if ("error" in draftResult && draftResult.error) {
        console.error(
          "⚠️ Draft cards error (preserving existing data):",
          draftResult.error,
        );
      }

      if (
        developerResult.success &&
        "data" in developerResult &&
        Array.isArray(developerResult.data)
      ) {
        setDeveloperCards(developerResult.data);

        // ✅ NEW: Notify about all processed cards
        developerResult.data.forEach((card: any) => {
          window.dispatchEvent(
            new CustomEvent("card-processed-notification", {
              detail: { cardId: card.id },
            }),
          );
        });
        console.log(
          `✅ ${developerResult.data.length} developer cards processed, notifications sent`,
        );
      } else if ("error" in developerResult && developerResult.error) {
        console.error(
          "⚠️ Developer cards error (preserving existing data):",
          developerResult.error,
        );
      }

      console.log("✅ All data loaded and state updated");
    } catch (error) {
      console.error("❌ Critical error in fetchAllData:", error);
    } finally {
      setInitialDataLoaded(true);
      setIsLoadingShared(false);
      setIsLoadingDrafts(false);
      setIsLoadingDeveloper(false);
      console.log("✅ Loading states cleared");
    }
  }, [currentWorkspaceId, currentUserEmail]);

  // ✅ NEW: Initial data load - only runs once when workspace is ready
  useEffect(() => {
    if (currentWorkspaceId && !initialDataLoaded) {
      console.log("🚀 Initial dashboard data load");
      fetchAllData();
    }
  }, [currentWorkspaceId, initialDataLoaded, fetchAllData]);

  // On mount: clean up legacy onboarding flag if present
  useEffect(() => {
    localStorage.removeItem("showBetaPopupOnce");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show beta popup based purely on server-side status
  useEffect(() => {
    if (initialDataLoaded) {
      getTourGuideStatus().then((completedKeys) => {
        if (!completedKeys.includes(String(TOUR_GUIDE_KEYS.BETA_POPUP))) {
          setShowBetaPopup(true);
        }
      });
    }
  }, [initialDataLoaded]);

  // ✅ NEW: Refresh data only when explicitly requested
  useEffect(() => {
    if (shouldRefreshData && currentWorkspaceId) {
      console.log("♻️ Refreshing dashboard data (explicit request)");
      fetchAllData();
      setShouldRefreshData(false);
    }
  }, [shouldRefreshData, currentWorkspaceId, fetchAllData]);

  // Tab switching handler - only switches tab, no automatic refresh
  const handleTabSwitch = (tab: "all" | "created" | "draft" | "shared") => {
  console.log("📑 Switching to tab:", tab, "- PiP remains visible");

  // ✅ Close other views but NOT PiP mode
  if (showWorkspace || showViewFeedback || showUploadWorkspace) {
    console.log("🧹 Closing open views before tab switch...");
    setShowWorkspace(false);
    setShowViewFeedback(false);
    setShowUploadWorkspace(false);
    // ❌ DON'T close PiP mode: setIsPiPMode(false);
  }

  setActiveTab(tab);
};

  // Manual refresh function for pull-to-refresh or explicit refresh button
  const refreshCurrentTab = async () => {
    console.log("🔄 Manual refresh requested for tab:", activeTab);

    if (activeTab === "shared") {
      setIsLoadingDeveloper(true);
      try {
        let result;
        if (currentUserEmail && EnhancedTokenManager.getAccessToken()) {
          result =
            await fetchDeveloperFeedbackCardsWithCredentials(currentUserEmail);
        }

        // ✅ FIXED: Type-safe check
        if (result && result.success && "data" in result && result.data) {
          setDeveloperCards(result.data);
        }
      } catch (error) {
        console.error("Error refreshing developer feedback:", error);
      } finally {
        setIsLoadingDeveloper(false);
      }
    } else if (activeTab === "draft") {
      setIsLoadingDrafts(true);
      try {
        let draftResult;
        if (currentWorkspaceId) {
          draftResult = await fetchDraftCards(currentWorkspaceId);
        }

        // ✅ FIXED: Type-safe check
        if (
          draftResult &&
          draftResult.success &&
          "data" in draftResult &&
          draftResult.data
        ) {
          setDraftCards(draftResult.data);
        }
      } catch (error) {
        console.error("Error refreshing draft cards:", error);
      } finally {
        setIsLoadingDrafts(false);
      }
    } else if (activeTab === "all" || activeTab === "created") {
      setIsLoadingShared(true);
      try {
        let sharedResult;
        if (currentWorkspaceId) {
          sharedResult = await fetchSharedFeedbackCards(currentWorkspaceId);
        }

        // ✅ FIXED: Type-safe check
        if (
          sharedResult &&
          sharedResult.success &&
          "data" in sharedResult &&
          sharedResult.data
        ) {
          setSharedFeedbackCards(sharedResult.data);
        }
      } catch (error) {
        console.error("Error refreshing shared feedback cards:", error);
      } finally {
        setIsLoadingShared(false);
      }
    }
  };

  // Handler functions - work for both regular users and developer credentials
  const handleOpenPopup = () => {
    if (!currentWorkspaceId) {
      setInitializationError("Workspace not initialized. Please try again.");
      return;
    }
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => setIsPopupOpen(false);

  const handleOpenUploadPopup = () => {
    if (!currentWorkspaceId) {
      setInitializationError("Workspace not initialized. Please try again.");
      return;
    }
    setIsUploadPopupOpen(true);
  };

  const handleCloseUploadPopup = () => {
    setIsUploadPopupOpen(false);
  };

  const handleContinue = async (
    feedbackData: FeedbackData,
    apiResponse?: CreateCardApiResponse,
  ) => {
    if (!currentWorkspaceId) {
      setInitializationError("Workspace not initialized. Please try again.");
      return;
    }

    if (apiResponse) {
      const timestamp = apiResponse.created_at || getCurrentISTTimestamp();
      const newDraftCard: EnhancedDraftCardData = {
        id: apiResponse.id,
        name: apiResponse.name,
        created_at: timestamp,
        imageUrl: generatePlaceholderImage(apiResponse.name),
        feedbackCount: 0,
        feedbackData: feedbackData,
        status: "draft",
        savedFeedbackItems: [],
        savedWorkspaceState: {
          screenshots: [],
          recordings: [],
          feedbackItemsOrder: [],
          showIframe: true,
        },
        lastUpdated: timestamp,
        allApiItems: [],
      };
      setDraftCards((prev) => [newDraftCard, ...prev]);

      // Pass card data via navigation state so WorkspacePage opens instantly
      // without waiting for GET_DRAFT to return the newly created card
      navigate(`/dashboard/workspace/${buildCardUrl(apiResponse.id, apiResponse.name)}`, {
        state: {
          newCard: {
            cardName: apiResponse.name,
            cardId: apiResponse.id,
            workspaceId: currentWorkspaceId,
          },
        },
      });
    }

    setIsPopupOpen(false);
  };
  const handleUploadContinue = async (
    feedbackData: FeedbackData,
    apiResponse?: CreateCardApiResponse,
  ) => {
    if (!currentWorkspaceId) {
      setInitializationError("Workspace not initialized. Please try again.");
      return;
    }

    if (apiResponse) {
      const timestamp = apiResponse.created_at || getCurrentISTTimestamp();
      const newDraftCard: EnhancedDraftCardData = {
        id: apiResponse.id,
        name: apiResponse.name,
        created_at: timestamp,
        imageUrl: generatePlaceholderImage(apiResponse.name),
        feedbackCount: 0,
        feedbackData: feedbackData,
        status: "draft",
        savedFeedbackItems: [],
        savedWorkspaceState: {
          screenshots: [],
          recordings: [],
          feedbackItemsOrder: [],
          showIframe: false,
        },
        lastUpdated: timestamp,
        allApiItems: [],
      };
      setDraftCards((prev) => [newDraftCard, ...prev]);

      navigate(`/dashboard/upload/${buildCardUrl(apiResponse.id, apiResponse.name)}`, {
        state: {
          newCard: {
            cardName: apiResponse.name,
            cardId: apiResponse.id,
            workspaceId: currentWorkspaceId,
          },
        },
      });
    }

    setIsUploadPopupOpen(false);
  };

  const handleSaveAndExit = (
    orderedFeedbackItems: FeedbackItem[],
    workspaceStateData?: WorkspaceState,
  ) => {
    const itemsWithStableIds = orderedFeedbackItems
      .map((item, index) => ({
        ...item,
        id:
          item.id ||
          `feedback_${currentApiResponse?.id}_${index}_${item.timestamp}`,
      }))
      .sort((a, b) => a.timestamp - b.timestamp);

    if (currentApiResponse?.id) {
      const firstScreenshot = itemsWithStableIds.find(
        (item) => item.type === "screenshot" && item.annotatedImage,
      );
      const previewImage = firstScreenshot?.annotatedImage || "";

      setDraftCards((prev) =>
        prev.map((card) => {
          if (card.id === currentApiResponse.id) {
            return {
              ...card,
              feedbackCount: itemsWithStableIds.length,
              status: "draft" as const,
              savedFeedbackItems: itemsWithStableIds,
              savedWorkspaceState: workspaceStateData,
              lastUpdated: getCurrentISTTimestamp(),
              imageUrl: previewImage || card.imageUrl,
            };
          }
          return card;
        }),
      );
    }

    // ✅ CRITICAL: Save workspace state and feedback items BEFORE showing ViewFeedback
    if (workspaceStateData) {
      setWorkspaceState(workspaceStateData);
    }
    setFeedbackItems(itemsWithStableIds);

    // ✅ IMPORTANT: Keep isViewOnlyMode as FALSE for draft editing flow
    // User can still add more feedback or go back to workspace
    setIsViewOnlyMode(false);

    // ✅ Show ViewFeedback, but keep workspace data in memory
    setShowWorkspace(false);
    setShowViewFeedback(true);
    onHideSidebar?.(false);

    console.log(
      "✅ Save & Exit: Staying in edit mode (isViewOnlyMode=false), can return to workspace or share",
    );
  };

  const handleUploadSaveAndExit = (
    orderedFeedbackItems: FeedbackItem[],
    workspaceStateData?: WorkspaceState,
  ) => {
    const itemsWithStableIds = orderedFeedbackItems.map((item, index) => ({
      ...item,
      id:
        item.id ||
        `feedback_${uploadApiResponse?.id}_${index}_${item.timestamp}`,
    }));

    if (uploadApiResponse?.id) {
      const firstScreenshot = itemsWithStableIds.find(
        (item) => item.type === "screenshot" && item.annotatedImage,
      );
      const previewImage = firstScreenshot?.annotatedImage || "";

      setDraftCards((prev) =>
        prev.map((card) => {
          if (card.id === uploadApiResponse.id) {
            return {
              ...card,
              feedbackCount: itemsWithStableIds.length,
              status: "draft" as const,
              savedFeedbackItems: itemsWithStableIds,
              savedWorkspaceState: workspaceStateData,
              lastUpdated: getCurrentISTTimestamp(),
              imageUrl: previewImage || card.imageUrl,
            };
          }
          return card;
        }),
      );
    }

    if (workspaceStateData) {
      setWorkspaceState(workspaceStateData);
    }
    setFeedbackItems(itemsWithStableIds);
    setIsViewOnlyMode(false);

    setShowUploadWorkspace(false);
    setShowViewFeedback(true);
    onHideSidebar?.(false);

    console.log("✅ Upload workspace saved, showing view feedback");
  };

  const handleUploadSaveDraft = (
    orderedFeedbackItems: FeedbackItem[],
    workspaceStateData?: WorkspaceState,
  ) => {
    const itemsWithStableIds = orderedFeedbackItems.map((item, index) => ({
      ...item,
      id:
        item.id ||
        `feedback_${uploadApiResponse?.id}_${index}_${item.timestamp}`,
    }));

    if (uploadApiResponse?.id) {
      const firstScreenshot = itemsWithStableIds.find(
        (item) => item.type === "screenshot" && item.annotatedImage,
      );
      const previewImage = firstScreenshot?.annotatedImage || "";

      setDraftCards((prev) =>
        prev.map((card) => {
          if (card.id === uploadApiResponse.id) {
            return {
              ...card,
              feedbackCount: itemsWithStableIds.length,
              status: "draft" as const,
              savedFeedbackItems: itemsWithStableIds,
              savedWorkspaceState: workspaceStateData,
              lastUpdated: getCurrentISTTimestamp(),
              imageUrl: previewImage || card.imageUrl,
            };
          }
          return card;
        }),
      );
    }

    if (workspaceStateData) {
      setWorkspaceState(workspaceStateData);
    }
    setFeedbackItems(itemsWithStableIds);

    setShowUploadWorkspace(false);
    setActiveTab("draft");
    onHideSidebar?.(false);
    navigate('/dashboard', { replace: true });

    setSuccessMessageText(
      `"${uploadFeedbackData?.name || "Feedback"}" has been saved to drafts!`,
    );
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);

    // ✅ IMPROVED: Force refresh draft tab
    if (currentWorkspaceId) {
      console.log("♻️ Triggering draft tab refresh after upload save...");

      window.dispatchEvent(new CustomEvent("refresh-draft-tab"));

      setTimeout(() => {
        setIsLoadingDrafts(true);
        fetchDraftCards(currentWorkspaceId).then((result) => {
          if (result.success && "data" in result && result.data) {
            setDraftCards(result.data);
            console.log("✅ Draft tab refreshed successfully");
          }
          setIsLoadingDrafts(false);
        });
      }, 500);
    }
  };

  const handleUploadBack = (deletionState?: {
    cardDeleted: boolean;
    deletedCardId: number;
    deletedCardName: string;
    activeTab: "draft";
  }) => {
    console.log("⬅️ Navigating back from upload workspace");

    if (deletionState?.cardDeleted) {
      console.log("🗑️ Card was deleted from upload workspace:", {
        cardId: deletionState.deletedCardId,
        cardName: deletionState.deletedCardName,
      });

      setDraftCards((prev) =>
        prev.filter((card) => card.id !== deletionState.deletedCardId),
      );

      setSuccessMessageText(
        `"${deletionState.deletedCardName}" has been deleted successfully!`,
      );
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      setActiveTab(deletionState.activeTab);
      window.dispatchEvent(new CustomEvent("refresh-draft-tab"));
    }

    setShowUploadWorkspace(false);
    setUploadFeedbackData(null);
    setUploadApiResponse(null);
    onHideSidebar?.(false);
    navigate('/dashboard', { replace: true });

    console.log("✅ Returned to dashboard from upload workspace");
  };


  const handleBackToWorkspaceFromViewFeedback = useCallback(() => {
    console.log("⬅️ Returning to FeedbackWorkspace from ViewFeedback");

    // ✅ STEP 1: Navigate immediately (no delay)
    setShowViewFeedback(false);
    setIsOpenedFromDraftCard(false);
    setShowWorkspace(true);
    onHideSidebar?.(true);

    console.log("✅ Navigation completed instantly");

    // ✅ STEP 2: Fetch fresh data in background (non-blocking)
    if (currentApiResponse?.id && currentWorkspaceId) {
      const cardIdToRefresh = currentApiResponse.id;

      console.log("🔄 Fetching latest feedback data in background:", {
        cardId: cardIdToRefresh,
        currentFeedbackCount: feedbackItems.length,
      });

      // Fetch asynchronously without blocking
      (async () => {
        try {
          const accessToken = EnhancedTokenManager.getAccessToken();
          if (!accessToken) {
            console.warn("⚠️ No access token for background refresh");
            return;
          }

          // Fetch fresh data from API
          const response = await fetch(
            API_URLS.GET_FULL_CARDS_FEEDBACK_STATUS_UPDATE(cardIdToRefresh),
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
            },
          );

          if (!response.ok) {
            console.warn("⚠️ Background refresh failed:", response.status);
            return;
          }

          const data = await response.json();
          console.log("✅ Background refresh - fresh data received:", {
            feedbackCount: data.feedbacks?.length || 0,
          });

          // Transform API response to FeedbackItem format
          if (data.feedbacks && Array.isArray(data.feedbacks)) {
            const transformedItems: FeedbackItem[] = await Promise.all(
              data.feedbacks
                .filter(
                  (fb: any) =>
                    fb.feedback_message ||
                    fb.feedback_screenshot ||
                    fb.feedback_recording ||
                    fb.feedback_voice,
                )
                .map(async (fb: any, index: number) => {
                  const feedbackId = fb.feedback_id.toString();

                  // Determine feedback type
                  let itemType: "screenshot" | "recording" = "screenshot";
                  if (fb.feedback_recording && !fb.feedback_screenshot) {
                    itemType = "recording";
                  } else if (fb.feedback_recording && fb.feedback_screenshot) {
                    itemType = "recording";
                  }

                  const feedbackItem: FeedbackItem = {
                    id: feedbackId,
                    type: itemType,
                    timestamp: new Date(fb.created_at).getTime(),
                    order: index + 1,
                    feedback: fb.feedback_message || "",
                    hasAudio: !!fb.feedback_voice,
                    audioUrl: fb.feedback_voice
                      ? `${API_URLS.GET_FILE_VOICE(feedbackId)}`
                      : undefined,
                    audioFilename: fb.feedback_voice
                      ? `audio_${fb.feedback_id}.mp3`
                      : undefined,
                    user: {
                      name: "User",
                      avatar: AvatarIcon,
                    },
                    isSelected: false,
                    apiFeedbackId: fb.feedback_id,
                    uniqueFeedbackId: feedbackId,
                  };

                  // Handle screenshot
                  if (itemType === "screenshot" && fb.feedback_screenshot) {
                    const screenshotUrl = `${BASE_URL}${fb.feedback_screenshot}`;
                    feedbackItem.annotatedImage = screenshotUrl;
                    feedbackItem.originalImage = screenshotUrl;
                  }

                  // Handle recording
                  if (itemType === "recording" && fb.feedback_recording) {
                    const recordingUrl = `${BASE_URL}${fb.feedback_recording}`;
                    feedbackItem.dataUrl = recordingUrl;
                    feedbackItem.duration = 30;

                    // Load recording blob asynchronously (non-blocking)
                    fetch(recordingUrl)
                      .then((blobResponse) => blobResponse.blob())
                      .then((blob) => {
                        // Update the blob later when it's loaded
                        setFeedbackItems((prevItems) =>
                          prevItems.map((item) =>
                            item.id === feedbackId
                              ? { ...item, recordingBlob: blob }
                              : item,
                          ),
                        );
                      })
                      .catch((error) => {
                        console.warn(
                          `⚠️ Failed to load recording blob for ${feedbackId}:`,
                          error,
                        );
                      });

                    // Set placeholder blob for now
                    feedbackItem.recordingBlob = new Blob([], {
                      type: "video/mp4",
                    });

                    // Load thumbnail if available
                    if (fb.feedback_thumbnail) {
                      feedbackItem.thumbnailUrl = `${BASE_URL}${fb.feedback_thumbnail}`;
                    }
                  }

                  return feedbackItem;
                }),
            );

            // ✅ Sort by timestamp (oldest first, newest last)
            const sortedItems = transformedItems.sort(
              (a, b) => a.timestamp - b.timestamp,
            );

            console.log("✅ Background refresh - transformed items:", {
              totalItems: sortedItems.length,
              oldCount: feedbackItems.length,
              newItemsAdded: sortedItems.length - feedbackItems.length,
            });

            // ✅ Separate into screenshots and recordings
            const newScreenshots: ScreenshotSubmission[] = sortedItems
              .filter(
                (item) => item.type === "screenshot" && item.annotatedImage,
              )
              .map((item) => ({
                id: item.id,
                annotatedImage: item.annotatedImage!,
                originalImage: item.originalImage || item.annotatedImage!,
                feedback: item.feedback || "",
                timestamp: item.timestamp,
                hasAudio: item.hasAudio || false,
                audioUrl: item.audioUrl,
                audioFilename: item.audioFilename,
                uniqueFeedbackId: item.uniqueFeedbackId,
                apiFeedbackId: item.apiFeedbackId,
              }));

            const newRecordings: RecordingSubmission[] = sortedItems
              .filter((item) => item.type === "recording" && item.dataUrl)
              .map((item) => ({
                id: item.id,
                recordingBlob:
                  item.recordingBlob || new Blob([], { type: "video/mp4" }),
                dataUrl: item.dataUrl!,
                feedback: item.feedback || "",
                timestamp: item.timestamp,
                duration: item.duration || 30,
                hasAudio: false,
                uniqueFeedbackId: item.uniqueFeedbackId,
                apiFeedbackId: item.apiFeedbackId,
                thumbnailUrl: item.thumbnailUrl,
              }));

            // ✅ Create feedbackItemsOrder array (ascending = oldest first)
            const newOrder = sortedItems.map(
              (item) => `${item.type}-${item.id}`,
            );

            // Preserve the existing order so the sidebar doesn't reorder after the
            // background fetch completes. Only fall back to newOrder when there is
            // no saved order yet (e.g. a brand-new draft with no savedWorkspaceState).
            const preservedOrder =
              (workspaceState?.feedbackItemsOrder?.length ?? 0) > 0
                ? workspaceState!.feedbackItemsOrder
                : newOrder;

            // ✅ Update workspace state with fresh data
            const updatedWorkspaceState: WorkspaceState = {
              screenshots: newScreenshots,
              recordings: newRecordings,
              feedbackItemsOrder: preservedOrder,
              url: workspaceState?.url,
              iframeHistory: workspaceState?.iframeHistory,
              iframeHistoryIndex: workspaceState?.iframeHistoryIndex,
              showIframe: workspaceState?.showIframe,
              feedbackId: workspaceState?.feedbackId,
              isUrlLocked: sortedItems.length > 0,
            };

            // ✅ Update state with fresh data (workspace is already visible)
            setWorkspaceState(updatedWorkspaceState);
            setFeedbackItems(sortedItems);

            console.log("✅ Background refresh complete - workspace updated:", {
              screenshots: newScreenshots.length,
              recordings: newRecordings.length,
              totalItems: sortedItems.length,
            });
          }
        } catch (error) {
          console.warn("⚠️ Background refresh failed:", error);
          // Silently fail - workspace is already showing with old data
        }
      })();
    }
  }, [
    currentApiResponse,
    currentWorkspaceId,
    feedbackItems,
    workspaceState,
    onHideSidebar,
  ]);
  const handleBackToPopup = () => {
    setShowWorkspace(false);
    setIsPopupOpen(true);
    setWorkspaceState(null);
    setCurrentApiResponse(null);
    onHideSidebar?.(false);
  };

  const handleBackFromViewFeedback = (deletionState?: {
    cardDeleted: boolean;
    deletedCardId: number;
    deletedCardName: string;
    activeTab: "draft" | "all" | "shared";
    shareSuccess?: {
      // ✅ NEW: Add share success data
      cardId: number;
      feedbackName: string;
      emails: string[];
      feedbackCount: number;
    };
  }) => {
    const wasSharedView = useSharedFeedbackView;
    console.log("⬅️ Navigating back from view feedback to dashboard", {
      currentActiveTab: activeTab,
      deletionState: !!deletionState,
      shareSuccess: !!deletionState?.shareSuccess, // ✅ NEW
      currentViewStates: {
        showViewFeedback,
        showWorkspace,
        showUploadWorkspace,
        isPiPMode,
      },
    });

    // ✅ NEW: Handle share success - navigate to created tab
    if (deletionState?.shareSuccess) {
      console.log("✅ Share success detected - navigating to created tab:", {
        cardId: deletionState.shareSuccess.cardId,
        feedbackName: deletionState.shareSuccess.feedbackName,
        emails: deletionState.shareSuccess.emails,
        feedbackCount: deletionState.shareSuccess.feedbackCount,
      });

      // Close all views
      setShowViewFeedback(false);
      setShowWorkspace(false);
      setShowUploadWorkspace(false);
      setIsPiPMode(false);

      // Clear all temporary state
      setCurrentApiResponse(null);
      setFeedbackItems([]);
      setWorkspaceState(null);
      setIsViewOnlyMode(false);
      setUseSharedFeedbackView(false);

      // Show success message
      setSuccessMessageText(
        `"${deletionState.shareSuccess.feedbackName}" shared with ${deletionState.shareSuccess.emails.length} developer(s) successfully!`,
      );
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      // Navigate to created tab
      setActiveTab("created");
      onHideSidebar?.(false);
      navigate('/dashboard', { replace: true });

      // Trigger refresh of created tab
      console.log("♻️ Triggering refresh of created tab...");
      setShouldRefreshData(true);

      return;
    }

    // Handle card deletion
    if (deletionState?.cardDeleted) {
      console.log("🗑️ Card was deleted:", {
        cardId: deletionState.deletedCardId,
        cardName: deletionState.deletedCardName,
      });

      setDraftCards((prev) =>
        prev.filter((card) => card.id !== deletionState.deletedCardId),
      );

      setSuccessMessageText(
        `"${deletionState.deletedCardName}" has been deleted successfully!`,
      );
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      if (deletionState.activeTab) {
        setActiveTab(deletionState.activeTab);
      }

      window.dispatchEvent(new CustomEvent("refresh-draft-tab"));
    }

    // ✅ CRITICAL FIX: Close ALL views and clear ALL state in a SINGLE batch
    console.log("🧹 Cleaning up ALL view states...");

    setShowViewFeedback(() => false);
    setShowWorkspace(() => false);
    setShowUploadWorkspace(() => false);
    setIsPiPMode(() => false);

    setCurrentApiResponse(() => null);
    setFeedbackItems(() => []);
    setWorkspaceState(() => null);
    setUploadFeedbackData(() => null);
    setUploadApiResponse(() => null);
    setPipWorkspaceData(() => null);

    setIsViewOnlyMode(() => false);
    setUseSharedFeedbackView(() => false);

    onHideSidebar?.(false);

    if (wasSharedView && !deletionState) {
      setActiveTab("shared");
    }

    navigate('/dashboard', { replace: true });

    console.log(
      "✅ Cleanup complete - all states reset, staying on tab:",
      activeTab,
    );
  };

  const handleBackFromDraftCardToHome = useCallback(() => {
    setShowViewFeedback(false);
    setShowWorkspace(false);
    setCurrentApiResponse(null);
    setFeedbackItems([]);
    setWorkspaceState(null);
    setIsViewOnlyMode(false);
    setUseSharedFeedbackView(false);
    setIsOpenedFromDraftCard(false);
    setActiveTab("draft");
    onHideSidebar?.(false);
    navigate('/dashboard', { replace: true });
  }, [onHideSidebar]);

  const handleShareWithDeveloper = async (shareData?: {
    success: boolean;
    cardId: number;
    feedbackName: string;
    emails: string[];
    feedbackCount: number;
    sharedAt?: string;
  }) => {
    if (shareData?.success && shareData.cardId) {
      const draftCard = draftCards.find((card) => card.id === shareData.cardId);
      if (draftCard) {
        const actualSharedAt = shareData.sharedAt || getCurrentISTTimestamp();
        const actualDaysAgo = calculateDaysAgo(actualSharedAt);

        const sharedCard: EnhancedSharedFeedbackCard = {
          id: shareData.cardId,
          name: shareData.feedbackName,
          created_at: draftCard.created_at,
          imageUrl: draftCard.imageUrl,
          feedbackCount: shareData.feedbackCount,
          status: "Invited",
          sharedWith: shareData.emails,
          sharedAt: actualSharedAt,
          daysAgo: actualDaysAgo,
          allApiItems: [],
          lastUpdated: getCurrentISTTimestamp(),
          isDeveloperFeedback: false,
          workspaceOwnerName: currentUserName || undefined,
          workspaceOwnerProfile: profileImage || null,
          preloadedDevelopers: shareData.emails.map((email) => ({
            email,
            name: null,
            profile_image: null,
          })),
        };

        setDraftCards((prev) =>
          prev.filter((card) => card.id !== shareData.cardId),
        );
        setSharedFeedbackCards((prev) => [sharedCard, ...prev]);

        setSuccessMessageText(
          `"${shareData.feedbackName}" has been successfully shared with ${
            shareData.emails.length
          } developer${shareData.emails.length !== 1 ? "s" : ""}!`,
        );
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 5000);
      }

      setShowViewFeedback(false);
      setShowWorkspace(false);
      setCurrentApiResponse(null);
      setFeedbackItems([]);
      setWorkspaceState(null);
      onHideSidebar?.(false);
      setActiveTab("created");
      navigate('/dashboard', { replace: true });

      // ✅ CHANGED: Request refresh instead of immediate fetch
      console.log("📤 Share completed, requesting data refresh");
      setShouldRefreshData(true);
    }
  };

  const onUpdateFeedback = (itemId: string, newFeedback: string) => {
    setFeedbackItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, feedback: newFeedback } : item,
      ),
    );
  };

  const onUpdateAudio = (
    feedbackId: string,
    hasAudio: boolean,
    audioUrl?: string,
  ) => {
    setFeedbackItems((prev) =>
      prev.map((item) =>
        item.id === feedbackId
          ? {
              ...item,
              hasAudio,
              audioUrl,
              audioFilename: audioUrl ? `audio_${feedbackId}.wav` : undefined,
            }
          : item,
      ),
    );
  };

  const onDeleteFeedback = (itemId: string) => {
    setFeedbackItems((prev) => prev.filter((item) => item.id !== itemId));

    if (currentApiResponse?.id) {
      setDraftCards((prev) =>
        prev.map((card) => {
          if (card.id === currentApiResponse.id) {
            const updatedSavedItems =
              card.savedFeedbackItems?.filter((item) => item.id !== itemId) ||
              [];

            const updatedCard = {
              ...card,
              feedbackCount: Math.max(0, card.feedbackCount - 1),
              savedFeedbackItems: updatedSavedItems,
              lastUpdated: getCurrentISTTimestamp(),
            };
            return updatedCard;
          }
          return card;
        }),
      );
    }
  };

  const handleEditDraftCard = useCallback(
    (cardId: number) => {
      markCardNotificationsAsRead(cardId);
      const card = draftCards.find((c) => c.id === cardId);
      const cardName = card?.name || '';
      navigate(`/dashboard/feedback/${buildCardUrl(cardId, cardName)}`, { state: { activeTab: "draft" } });
    },
    [navigate, draftCards],
  );
  const handleShareDraftCard = (cardId: number) => {
    const draftCard = draftCards.find((card) => card.id === cardId);
    if (draftCard) {
      setCurrentShareCard(draftCard);
      setIsSharePopupOpen(true);
    }
  };

  const handleSharePopupSubmit = async (emails: string[]) => {
    if (!currentShareCard) return;

    setIsSharePopupOpen(false);

    try {
      const result = await shareFeedbackWithDeveloper(
        currentShareCard.id,
        emails,
        currentWorkspaceId || undefined,
      );

      if (result.success && result.data) {
        const feedbackName = currentShareCard.name;
        const emailCount = emails.length;

        const actualSharedAt = (() => {
          if (!result.data) return getCurrentISTTimestamp();
          const data = result.data as typeof result.data & {
            shared_at?: string;
            sent_at?: string;
          };
          return data.shared_at || data.sent_at || getCurrentISTTimestamp();
        })();
        const actualDaysAgo = calculateDaysAgo(actualSharedAt);

        setSuccessMessageText(
          `"${feedbackName}" has been successfully shared with ${emailCount} developer${
            emailCount !== 1 ? "s" : ""
          }!`,
        );
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);

        const sharedCard: EnhancedSharedFeedbackCard = {
          id: currentShareCard.id,
          name: currentShareCard.name,
          created_at: currentShareCard.created_at,
          imageUrl: currentShareCard.imageUrl,
          feedbackCount: currentShareCard.feedbackCount,
          status: "Invited",
          sharedWith: emails,
          sharedAt: actualSharedAt,
          daysAgo: actualDaysAgo,
          lastUpdated: getCurrentISTTimestamp(),
          allApiItems: [],
          isDeveloperFeedback: false,
          workspaceOwnerName: currentUserName || undefined,
          workspaceOwnerProfile: profileImage || null,
          preloadedDevelopers: emails.map((email) => ({
            email,
            name: null,
            profile_image: null,
          })),
        };

        setDraftCards((prev) =>
          prev.filter((card) => card.id !== currentShareCard.id),
        );
        setSharedFeedbackCards((prev) => [sharedCard, ...prev]);
        setActiveTab("created");
      } else {
        throw new Error(result.error || "Share failed");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      alert(`Failed to share feedback: ${errorMessage}`);
      setIsSharePopupOpen(true);
    } finally {
      setCurrentShareCard(null);
    }
  };

  const handleSharePopupCancel = () => {
    setIsSharePopupOpen(false);
    setCurrentShareCard(null);
  };

  const handleDeleteDraftCard = async (cardId: number) => {
    try {
      // ✅ REMOVED: window.confirm alert - DraftCard now handles confirmation popup

      const result = await deleteCard(cardId);

      if (result.success) {
        setDraftCards((prev) => prev.filter((card) => card.id !== cardId));
        setSuccessMessageText("Draft deleted successfully!");
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);

        // Request refresh instead of immediate fetch
        console.log("🗑️ Draft deleted, requesting data refresh");
        setShouldRefreshData(true);
      } else {
        alert(`Failed to delete draft: ${result.error}`);
      }
    } catch (error) {
      alert(
        `Error deleting draft: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  };

  const handleDeleteSharedFeedbackCard = async (cardId: number) => {
    try {
      const confirmed = window.confirm(
        "Are you sure you want to delete this shared feedback? This action cannot be undone.",
      );

      if (!confirmed) return;

      const result = await deleteCard(cardId);

      if (result.success) {
        setSharedFeedbackCards((prev) =>
          prev.filter((card) => card.id !== cardId),
        );
        setSuccessMessageText("Shared feedback deleted successfully!");
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);

        // ✅ CHANGED: Request refresh instead of immediate fetch
        console.log("🗑️ Shared feedback deleted, requesting data refresh");
        setShouldRefreshData(true);
      } else {
        alert(`Failed to delete shared feedback: ${result.error}`);
      }
    } catch (error) {
      alert(
        `Error deleting shared feedback: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      );
    }
  };

  const handleViewSharedFeedback = (
    cardId: number,
    isFromDraft: boolean = false,
    prepassedCard?: EnhancedSharedFeedbackCard,
  ) => {
    if (!isFromDraft) markCardNotificationsAsRead(cardId);
    markCardAsViewed(cardId);

    const card =
      prepassedCard ||
      sharedFeedbackCards.find((c) => c.id === cardId) ||
      developerCards.find((c) => c.id === cardId);

    const isDeveloper =
      card?.isDeveloperFeedback || developerCards.some((c) => c.id === cardId);

    const cardName = card?.name || '';
    navigate(
      `/dashboard/feedback/${buildCardUrl(cardId, cardName)}${isDeveloper ? "?view=developer" : ""}`,
    );
  };

  const handleFeedbackStatusChange = (
    cardId: number,
    newStatus: "Invited" | "Viewed" | "In progress" | "Done" | "New" | "Shared with me",
  ) => {
    setSharedFeedbackCards((prev) =>
      prev.map((card) => {
        if (
          card.id === cardId &&
          newStatus !== "New" &&
          newStatus !== "Shared with me" && // ✅ Don't allow changing to "Shared with me"
          !card.isDeveloperFeedback
        ) {
          return {
            ...card,
            status: newStatus as "Invited" | "Viewed" | "In progress" | "Done",
            lastUpdated: getCurrentISTTimestamp(),
          };
        }
        return card;
      }),
    );
  };

  const handleCloseSuccessMessage = useCallback(() => {
    setShowSuccessMessage(false);
  }, []);

  // Success message effect
  useEffect(() => {
    const navigationState = location.state as OnboardingStateData | null;

    // ✅ Handle activeTab state from navigation (from deep link back button)
    if (navigationState?.activeTab) {
      console.log(
        "📍 Setting active tab from navigation:",
        navigationState.activeTab,
      );
      setActiveTab(navigationState.activeTab);

      // ✅ NEW: Check if we should preserve data (don't reload)
      if (navigationState.preserveData) {
        console.log("💾 Preserving cached data, skipping reload");
        window.history.replaceState({}, document.title, location.pathname);
        return;
      }

      // Clear the state to prevent re-triggering on refresh
      window.history.replaceState({}, document.title, location.pathname);
    }

    if (navigationState?.fromShare) {
      if (navigationState.showSidebar) {
        onHideSidebar?.(false);
      }

      const feedbackName = navigationState.sharedFeedbackName || "feedback";
      const emailCount = navigationState.sharedEmails?.length || 0;

      setSuccessMessageText(
        `"${feedbackName}" has been successfully shared with ${emailCount} developer${
          emailCount !== 1 ? "s" : ""
        }!`,
      );
      setShowSuccessMessage(true);

      // ✅ CHANGED: Only refresh if explicitly requested
      if (navigationState.refreshCards && currentWorkspaceId) {
        console.log("🔄 Refresh requested from navigation state");
        setShouldRefreshData(true);
      }

      window.history.replaceState({}, document.title, location.pathname);

      const timeout = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [location.state, location.pathname, onHideSidebar, currentWorkspaceId]);

  // Load viewed cards from localStorage on mount
  useEffect(() => {
    const storedViewedCards = localStorage.getItem("viewed_developer_cards");
    if (storedViewedCards) {
      try {
        const parsed = JSON.parse(storedViewedCards);
        setViewedCards(new Set(parsed));
      } catch (error) {
        console.error("Error loading viewed cards:", error);
      }
    }
  }, []);

  useEffect(() => {
    // ✅ NEW: Listen for draft refresh events from localStorage
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "refresh_draft_tab" && e.newValue === "true") {
        console.log("🔄 Draft refresh triggered from storage event");

        if (currentWorkspaceId) {
          setIsLoadingDrafts(true);
          fetchDraftCards(currentWorkspaceId).then((result) => {
            if (result.success && "data" in result && result.data) {
              setDraftCards(result.data);
              console.log("✅ Draft tab refreshed from storage event");
            }
            setIsLoadingDrafts(false);
          });
        }

        // Clear the flag
        localStorage.removeItem("refresh_draft_tab");
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [currentWorkspaceId]);

  //  Listen for new notifications and inject cards immediately
  useEffect(() => {
    const handleNewNotification = async (event: Event) => {
      if (!(event instanceof CustomEvent)) {
        console.warn("⚠️ Event is not a CustomEvent");
        return;
      }

      console.log(
        "📬 New notification event received, checking for instant cards...",
      );

      const allKeys = Object.keys(sessionStorage);
      const instantCardKeys = allKeys.filter((key) =>
        key.startsWith("instant_card_"),
      );

      if (instantCardKeys.length > 0) {
        console.log(
          `✅ Found ${instantCardKeys.length} instant card(s) to inject`,
        );

        const newCards: EnhancedSharedFeedbackCard[] = [];

        for (const key of instantCardKeys) {
          const cardDataStr = sessionStorage.getItem(key);
          if (cardDataStr) {
            try {
              const instantCardData = JSON.parse(cardDataStr);
              const cardId = instantCardData.cardId;

              console.log(`📦 Processing instant card ${cardId}:`, {
                hasFeedbacks: !!instantCardData.fullFeedbacks,
                feedbackCount: instantCardData.fullFeedbacks?.length || 0,
                hasDevelopers: !!instantCardData.developers,
                developerCount: instantCardData.developers?.length || 0,
                cardCreatedAt: instantCardData.createdAt, // 3 days ago
                notificationSharedAt: instantCardData.sharedAt, // ✅ Just now!
              });

              const cardExists = developerCards.some((c) => c.id === cardId);

              if (!cardExists) {
                console.log(
                  `✅ Creating instant card ${cardId} with SHARE timestamp`,
                );

                // ✅ FIX: Use sharedAt from notification (when it was actually shared)
                // If not available (old notifications), fall back to current time
                const actualSharedAt =
                  instantCardData.sharedAt || getCurrentISTTimestamp();

                console.log("🕐 Using SHARE timestamp for card:", {
                  cardId,
                  cardCreatedAt: instantCardData.createdAt, // 3 days ago (project creation)
                  notificationSharedAt: instantCardData.sharedAt, // Just now (when shared)
                  actualSharedAt: actualSharedAt,
                  calculatedTimeAgo: calculateDaysAgo(actualSharedAt), // Should be "Just now"
                });

                // ✅ CRITICAL: Create card with SHARE timestamp
                const tempCard: EnhancedSharedFeedbackCard = {
                  id: cardId,
                  name: instantCardData.cardName,
                  created_at: instantCardData.createdAt, // Keep original card creation time
                  imageUrl:
                    instantCardData.previewImage ||
                    generatePlaceholderImage(instantCardData.cardName),
                  feedbackCount: instantCardData.feedbackCount,
                  status: "Shared with me" as const,
                  sharedWith: [],
                  sharedAt: actualSharedAt, // ✅ FIX: Use notification share time, not card creation time
                  daysAgo: calculateDaysAgo(actualSharedAt), // ✅ FIX: Calculate from share time
                  allApiItems: instantCardData.fullFeedbacks || [],
                  preloadedDevelopers: instantCardData.developers || [],
                  lastUpdated: actualSharedAt,
                  isDeveloperFeedback: true,
                  isRecentlyShared: true,
                  preloadedStatuses: {},
                };

                console.log(`✅ Instant card created with CORRECT timestamp:`, {
                  cardId,
                  sharedAt: tempCard.sharedAt,
                  daysAgo: tempCard.daysAgo, // Should now show "Just now" instead of "3 days ago"
                  originalCreation: tempCard.created_at, // 3 days ago (preserved for reference)
                });

                newCards.push(tempCard);
              }
            } catch (error) {
              console.error("❌ Error parsing instant card data:", error);
            }
          }
        }

        if (newCards.length > 0) {
          setDeveloperCards((prev) => [...newCards, ...prev]);
          console.log(
            `✅ Injected ${newCards.length} card(s) with CORRECT share timestamps`,
          );
        }
      }
    };

    window.addEventListener(
      "new-notification-received",
      handleNewNotification as EventListener,
    );

    return () => {
      window.removeEventListener(
        "new-notification-received",
        handleNewNotification as EventListener,
      );
    };
  }, [developerCards, generatePlaceholderImage]);
  // Listen for reshare refresh requests (background, show notification after)
  useEffect(() => {
    const handleRefreshCardFromReshare = async (event: Event) => {
      if (!(event instanceof CustomEvent)) return;

      const { cardId, feedbackId, notificationId, sharedBy, sharedAt } =
        event.detail;

      console.log("🔄 Reshare: Refreshing card data in background", {
        cardId,
        feedbackId,
      });

      if (!currentUserEmail) {
        console.warn("⚠️ No user email for refresh");
        return;
      }

      try {
        // Fetch fresh card data from developer API
        const result =
          await fetchDeveloperFeedbackCardsWithCredentials(currentUserEmail);

        if (result.success && result.data) {
          setDeveloperCards(result.data);
          console.log("✅ Card data refreshed successfully from reshare");

          // Find the refreshed card
          const refreshedCard = result.data.find((c: any) => c.id === cardId);

          if (refreshedCard) {
            // Store card data in sessionStorage for instant display
            sessionStorage.setItem(
              `instant_card_${cardId}`,
              JSON.stringify({
                cardId: refreshedCard.id,
                cardName: refreshedCard.name,
                feedbackCount: refreshedCard.feedbackCount,
                previewImage: refreshedCard.imageUrl,
                createdAt: refreshedCard.created_at,
                fullFeedbacks: refreshedCard.allApiItems || [],
                developers: refreshedCard.preloadedDevelopers || [],
                instant: true,
              }),
            );

            console.log("✅ Card data stored for instant display");

            // NOW show the notification by dispatching the open event
            setTimeout(() => {
              // Set highlight flags
              localStorage.setItem(
                "highlight_feedback_id",
                feedbackId.toString(),
              );
              localStorage.setItem("feedback_reshare_action", "reshared");

              // Dispatch event to open the card
              const openEvent = new CustomEvent(
                "open-shared-card-from-notification",
                {
                  detail: {
                    cardId,
                    feedbackId,
                    workspaceId: currentWorkspaceId, // ✅ FIXED: Use currentWorkspaceId from HomePage state
                    cardData: {
                      name: refreshedCard.name,
                      feedbackCount: refreshedCard.feedbackCount,
                      previewImage: refreshedCard.imageUrl,
                      createdAt: refreshedCard.created_at,
                      fullFeedbacks: refreshedCard.allApiItems || [],
                      developers: refreshedCard.preloadedDevelopers || [],
                    },
                    instant: true,
                    notificationId,
                    sharedBy,
                    sharedAt,
                    shouldHighlightFeedback: true,
                    isReshareEvent: true,
                  },
                },
              );

              window.dispatchEvent(openEvent);
              console.log("📬 Reshare notification shown after refresh");
            }, 300); // Small delay to ensure state is updated
          }
        }
      } catch (error) {
        console.error("❌ Failed to refresh card data:", error);
      }
    };

    window.addEventListener(
      "refresh-card-from-reshare",
      handleRefreshCardFromReshare as EventListener,
    );

    return () => {
      window.removeEventListener(
        "refresh-card-from-reshare",
        handleRefreshCardFromReshare as EventListener,
      );
    };
  }, [
    currentUserEmail,
    currentWorkspaceId,
    fetchDeveloperFeedbackCardsWithCredentials,
  ]); // ✅ FIXED: Added currentWorkspaceId dependency

  //  Process notification events passed from DashboardScreen
  useEffect(() => {
    if (!pendingNotificationEvent) return;

    const { type, data } = pendingNotificationEvent;
    console.log("🎯 HomeScreen: Processing notification event:", {
      type,
      data,
      feedbackId: data.feedbackId,
    });

    if (type === "shared") {
      const {
        cardId,
        feedbackId,
        workspaceId,
        cardData,
        instant,
        isReshareEvent,
      } = data;

      // ✅ CRITICAL: Ensure feedbackId is a string
      const feedbackIdStr = String(feedbackId);

      if (feedbackIdStr) {
        console.log("🎯 Setting highlight flags for shared card:", {
          feedbackId: feedbackIdStr,
          cardId,
          isReshareEvent,
        });
        localStorage.setItem("highlight_feedback_id", feedbackIdStr);

        if (isReshareEvent) {
          localStorage.setItem("feedback_reshare_action", "reshared");
          // ✅ ADD THIS LINE - Mark card as reshared
          localStorage.setItem(`card_${cardId}_reshared`, "true");
          console.log("🔄 Reshare event - set reshare flag and card marker");
        }
      }

      // Mark card as viewed
      setViewedCards((prev) => {
        const newSet = new Set(prev);
        newSet.add(cardId);
        localStorage.setItem(
          "viewed_developer_cards",
          JSON.stringify(Array.from(newSet)),
        );
        return newSet;
      });

      // Close any open views and switch to shared tab
      setShowWorkspace(false);
      setShowViewFeedback(false);
      setShowUploadWorkspace(false);
      setIsPiPMode(false);
      onHideSidebar?.(false);
      setActiveTab("shared");

      console.log("🔄 Switched to shared tab, preparing to open card:", cardId);

      setTimeout(async () => {
        const instantCardKey = `instant_card_${cardId}`;
        const instantCardDataStr = sessionStorage.getItem(instantCardKey);

        if (instant && instantCardDataStr) {
          console.log("⚡⚡⚡ INSTANT: Using pre-loaded card data");

          try {
            const instantCardData = JSON.parse(instantCardDataStr);

            // ✅ FIX: Use sharedAt from notification, not createdAt from card
            const actualSharedAt =
              instantCardData.sharedAt || getCurrentISTTimestamp();

            console.log("🕐 Creating card with SHARE timestamp:", {
              cardId,
              cardCreatedAt: instantCardData.createdAt, // 3 days ago
              notificationSharedAt: instantCardData.sharedAt, // Just now
              actualSharedAt: actualSharedAt,
              daysAgo: calculateDaysAgo(actualSharedAt), // Should be "Just now"
            });

            const tempCard: EnhancedSharedFeedbackCard = {
              id: cardId,
              name: instantCardData.cardName,
              created_at: instantCardData.createdAt, // Keep original card creation
              imageUrl:
                instantCardData.previewImage ||
                generatePlaceholderImage(instantCardData.cardName),
              feedbackCount: instantCardData.feedbackCount,
              status: "Shared with me" as const,
              sharedWith: [],
              sharedAt: actualSharedAt, // ✅ FIX: Use notification share time
              daysAgo: calculateDaysAgo(actualSharedAt), // ✅ FIX: Should show "Just now"
              allApiItems: instantCardData.fullFeedbacks || [],
              lastUpdated: actualSharedAt,
              isDeveloperFeedback: true,
              isRecentlyShared: false,
              preloadedStatuses: {},
              preloadedDevelopers: instantCardData.developers || [],
            };

            console.log("✅ Temp card created with CORRECT timestamp:", {
              daysAgo: tempCard.daysAgo, // "Just now" instead of "3 days ago"
            });

            setDeveloperCards((prev) => {
              const exists = prev.some((c) => c.id === cardId);
              if (exists) {
                return prev.map((c) => {
                  if (c.id === cardId) {
                    return {
                      ...tempCard,
                      allApiItems:
                        (tempCard.allApiItems?.length ?? 0) > 0
                          ? tempCard.allApiItems
                          : c.allApiItems,
                      preloadedDevelopers:
                        (tempCard.preloadedDevelopers?.length ?? 0) > 0
                          ? tempCard.preloadedDevelopers
                          : c.preloadedDevelopers,
                    };
                  }
                  return c;
                });
              }

              window.dispatchEvent(
                new CustomEvent("card-processed-notification", {
                  detail: { cardId: tempCard.id },
                }),
              );
              console.log(
                `✅ New card added, notified DashboardScreen: ${tempCard.id}`,
              );

              return [tempCard, ...prev];
            });

            console.log(
              "✅ Opening SharedFeedbackView with highlight:",
              feedbackId,
            );
            handleViewSharedFeedback(cardId, false, tempCard);

            // Background fetch for fresh data
            if (currentUserEmail) {
              console.log("🔄 Background fetching full card data...");
              fetchDeveloperFeedbackCardsWithCredentials(currentUserEmail).then(
                (result) => {
                  if (result.success && result.data) {
                    setDeveloperCards(result.data);
                    console.log("✅ Background fetch complete");
                  }
                },
              );
            }

            sessionStorage.removeItem(instantCardKey);
          } catch (error) {
            console.error("❌ Error parsing instant card data:", error);
            sessionStorage.removeItem(instantCardKey);
          }
        } else {
          let card = developerCards.find(
            (c: EnhancedSharedFeedbackCard) => c.id === cardId,
          );

          if (card) {
            console.log("⚡ Card found in cache!");
            handleViewSharedFeedback(cardId, false);
          } else if (instant && cardData) {
            console.log("⚡ Using pre-loaded card data");

            const tempCard: EnhancedSharedFeedbackCard = {
              id: cardId,
              name: cardData.name,
              created_at: cardData.createdAt,
              imageUrl:
                cardData.previewImage ||
                generatePlaceholderImage(cardData.name),
              feedbackCount: cardData.feedbackCount,
              status: "Shared with me" as const,
              sharedWith: [],
              sharedAt: cardData.createdAt,
              daysAgo: calculateDaysAgo(cardData.createdAt),
              allApiItems: cardData.fullFeedbacks || [], // ✅ USE PRE-LOADED FEEDBACKS
              lastUpdated: new Date().toISOString(),
              isDeveloperFeedback: true,
              isRecentlyShared: false,
              preloadedStatuses: {},
              preloadedDevelopers: cardData.developers || [], // ✅ USE PRE-LOADED DEVELOPERS
            };

            // ✅ FIXED: Added type annotations
            setDeveloperCards((prev) => {
              const exists = prev.some((c) => c.id === cardId);
              if (exists) {
                return prev.map((c) => {
                  if (c.id === cardId) {
                    return {
                      ...tempCard,
                      allApiItems:
                        (tempCard.allApiItems?.length ?? 0) > 0
                          ? tempCard.allApiItems
                          : c.allApiItems,
                      preloadedDevelopers:
                        (tempCard.preloadedDevelopers?.length ?? 0) > 0
                          ? tempCard.preloadedDevelopers
                          : c.preloadedDevelopers,
                    };
                  }
                  return c;
                });
              }

              // ✅ NEW: Notify for new card
              window.dispatchEvent(
                new CustomEvent("card-processed-notification", {
                  detail: { cardId: tempCard.id },
                }),
              );
              console.log(
                `✅ New card added, notified DashboardScreen: ${tempCard.id}`,
              );

              return [tempCard, ...prev];
            });
            handleViewSharedFeedback(cardId, false, tempCard);

            if (currentUserEmail) {
              fetchDeveloperFeedbackCardsWithCredentials(currentUserEmail).then(
                (result) => {
                  if (result.success && result.data) {
                    setDeveloperCards(result.data);
                  }
                },
              );
            }
          } else {
            console.log("🔄 Fetching card from API...");

            if (!currentUserEmail) {
              console.error("❌ No user email available");
              alert(
                "Unable to open feedback card. Please try logging in again.",
              );
              return;
            }

            setIsLoadingDeveloper(true);
            const result =
              await fetchDeveloperFeedbackCardsWithCredentials(
                currentUserEmail,
              );

            if (result.success && result.data) {
              card = result.data.find(
                (c: EnhancedSharedFeedbackCard) => c.id === cardId,
              );
              if (card) {
                setDeveloperCards(result.data);
                handleViewSharedFeedback(cardId, false, card);
                console.log("✅ Opened card after API fetch");
              } else {
                console.error("❌ Card not found in API response");
                alert(
                  "Unable to open feedback card. It may have been deleted.",
                );
              }
            } else {
              console.error("❌ Failed to fetch cards:", result.error);
              alert("Failed to load feedback card. Please try again.");
            }
            setIsLoadingDeveloper(false);
          }
        }
      }, 100);
    } else if (type === "created") {
      const { cardId, feedbackId, action, cardData, instant } = data;

      // ✅ CRITICAL: Ensure feedbackId is a string
      const feedbackIdStr = String(feedbackId);

      if (feedbackIdStr) {
        console.log("🎯 Setting highlight flags for created card:", {
          feedbackId: feedbackIdStr,
          cardId,
          action,
        });
        localStorage.setItem("highlight_feedback_id", feedbackIdStr);
        localStorage.setItem("developer_action", action);
      }

      setShowWorkspace(false);
      setShowViewFeedback(false);
      setShowUploadWorkspace(false);
      setIsPiPMode(false);
      onHideSidebar?.(false);
      setActiveTab("created");

      console.log(
        "🔄 Switched to created tab, preparing to open card:",
        cardId,
      );

      setTimeout(async () => {
        const instantCardKey = `instant_card_${cardId}`;
        const instantCardDataStr = sessionStorage.getItem(instantCardKey);

        if (instant && instantCardDataStr) {
          console.log("⚡⚡⚡ INSTANT: Using pre-loaded created card data");

          try {
            const instantCardData = JSON.parse(instantCardDataStr);

            const tempCard: EnhancedSharedFeedbackCard = {
              id: cardId,
              name: instantCardData.cardName,
              created_at: instantCardData.createdAt,
              imageUrl:
                instantCardData.previewImage ||
                generatePlaceholderImage(instantCardData.cardName),
              feedbackCount: instantCardData.feedbackCount,
              status: "Invited" as const,
              sharedWith: [],
              sharedAt: instantCardData.createdAt,
              daysAgo: calculateDaysAgo(instantCardData.createdAt),
              allApiItems: instantCardData.fullFeedbacks || [], // ✅ USE PRE-LOADED FEEDBACKS
              lastUpdated: new Date().toISOString(),
              isDeveloperFeedback: false,
              hasBeenShared: true,
              preloadedStatuses: {},
              preloadedDevelopers: instantCardData.developers || [], // ✅ USE PRE-LOADED DEVELOPERS
            };

            // ✅ FIXED: Added type annotations
            setSharedFeedbackCards((prev) => {
              const exists = prev.some(
                (c: EnhancedSharedFeedbackCard) => c.id === cardId,
              );
              if (exists) {
                return prev.map((c: EnhancedSharedFeedbackCard) =>
                  c.id === cardId ? tempCard : c,
                );
              }
              return [tempCard, ...prev];
            });

            console.log(
              "✅ Opening ViewListFeedback with highlight:",
              feedbackId,
            );
            handleViewSharedFeedback(cardId, false, tempCard);

            if (currentWorkspaceId) {
              console.log("🔄 Background fetching full card data...");
              fetchSharedFeedbackCards(currentWorkspaceId).then((result) => {
                if (result.success && "data" in result && result.data) {
                  setSharedFeedbackCards(result.data);
                  console.log("✅ Background fetch complete");
                }
              });
            }

            sessionStorage.removeItem(instantCardKey);
          } catch (error) {
            console.error("❌ Error parsing instant card data:", error);
            sessionStorage.removeItem(instantCardKey);
          }
        } else {
          let card = sharedFeedbackCards.find(
            (c: EnhancedSharedFeedbackCard) => c.id === cardId,
          );

          if (card) {
            console.log("⚡ Card found in cache!");
            handleViewSharedFeedback(cardId, false);
          } else if (instant && cardData) {
            console.log("⚡ Using pre-loaded card data");

            const tempCard: EnhancedSharedFeedbackCard = {
              id: cardId,
              name: cardData.name,
              created_at: cardData.createdAt,
              imageUrl:
                cardData.previewImage ||
                generatePlaceholderImage(cardData.name),
              feedbackCount: cardData.feedbackCount,
              status: "Invited" as const,
              sharedWith: [],
              sharedAt: cardData.createdAt,
              daysAgo: calculateDaysAgo(cardData.createdAt),
              allApiItems: cardData.fullFeedbacks || [], // ✅ USE PRE-LOADED FEEDBACKS
              lastUpdated: new Date().toISOString(),
              isDeveloperFeedback: false,
              hasBeenShared: true,
              preloadedStatuses: {},
              preloadedDevelopers: cardData.developers || [], // ✅ USE PRE-LOADED DEVELOPERS
            };

            // ✅ FIXED: Added type annotations
            setSharedFeedbackCards((prev) => {
              const exists = prev.some(
                (c: EnhancedSharedFeedbackCard) => c.id === cardId,
              );
              if (exists) return prev;
              return [tempCard, ...prev];
            });

            handleViewSharedFeedback(cardId, false, tempCard);

            if (currentWorkspaceId) {
              fetchSharedFeedbackCards(currentWorkspaceId).then((result) => {
                if (result.success && "data" in result && result.data) {
                  setSharedFeedbackCards(result.data);
                }
              });
            }
          } else {
            console.log("🔄 Fetching card from API...");

            if (!currentWorkspaceId) {
              console.error("❌ No workspace ID available");
              alert("Unable to open feedback card.");
              return;
            }

            setIsLoadingShared(true);
            const result = await fetchSharedFeedbackCards(currentWorkspaceId);

            if (result.success && "data" in result && result.data) {
              card = result.data.find(
                (c: EnhancedSharedFeedbackCard) => c.id === cardId,
              );
              if (card) {
                setSharedFeedbackCards(result.data);
                handleViewSharedFeedback(cardId, false, card);
                console.log("✅ Opened card after API fetch");
              } else {
                console.error("❌ Card not found in API response");
                alert(
                  "Unable to open feedback card. It may have been deleted.",
                );
              }
            } else {
              console.error("❌ Failed to fetch cards:", result.error);
              alert("Failed to load feedback card. Please try again.");
            }
            setIsLoadingShared(false);
          }
        }
      }, 100);
    }

    if (onNotificationEventProcessed) {
      onNotificationEventProcessed();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pendingNotificationEvent,
    onNotificationEventProcessed,
    developerCards,
    sharedFeedbackCards,
    currentUserEmail,
    currentWorkspaceId,
  ]);

  useEffect(() => {
    // ✅ Listen for draft refresh events from FeedbackWorkspace
    const handleDraftRefresh = () => {
      console.log("🔄 Draft refresh event received");
      if (currentWorkspaceId) {
        console.log("♻️ Refreshing draft tab data...");
        setIsLoadingDrafts(true);
        fetchDraftCards(currentWorkspaceId).then((result) => {
          if (result.success && "data" in result && result.data) {
            setDraftCards(result.data);
            console.log("✅ Draft tab refreshed successfully");
          }
          setIsLoadingDrafts(false);
        });
      }
    };

    // ✅ NEW: Silent refresh for shared tab (no loading indicators)
    const handleSilentSharedRefresh = async () => {
      console.log("🔄 Silent shared tab refresh triggered");

      // Prevent concurrent refreshes
      if (isRefreshingRef.current) {
        console.log("⏭️ Refresh already in progress, skipping");
        return;
      }

      if (currentUserEmail) {
        try {
          isRefreshingRef.current = true;

          // ✅ Use fast fetch that skips status pre-fetching
          const result =
            await fetchDeveloperFeedbackCardsWithCredentials(currentUserEmail);

          if (result.success && result.data) {
            setDeveloperCards(result.data);
            console.log("✅ Shared tab refreshed silently (fast):", {
              cardCount: result.data.length,
              cardIds: result.data.map((c) => c.id),
              refreshTime: `${
                Date.now() -
                parseInt(localStorage.getItem("last_shared_refresh") || "0")
              }ms`,
            });

            // Store timestamp to track when refresh completed
            localStorage.setItem("last_shared_refresh", Date.now().toString());
          }
        } catch (error) {
          console.error("❌ Silent refresh failed:", error);
        } finally {
          // Always clear refresh flag
          isRefreshingRef.current = false;
        }
      }
    };

    // ✅ Listen for custom refresh events
    window.addEventListener("refresh-draft-tab", handleDraftRefresh);
    window.addEventListener(
      "refresh-shared-tab-silently",
      handleSilentSharedRefresh,
    );

    // ✅ Listen for localStorage changes (for cross-tab/component communication)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "refresh_draft_tab" && e.newValue === "true") {
        console.log("🔄 Draft refresh triggered from storage event");
        handleDraftRefresh();
        // Clear the flag
        localStorage.removeItem("refresh_draft_tab");
      }

      if (e.key === "draft_saved_timestamp") {
        console.log("🔄 Draft saved timestamp updated, refreshing...");
        handleDraftRefresh();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("refresh-draft-tab", handleDraftRefresh);
      window.removeEventListener(
        "refresh-shared-tab-silently",
        handleSilentSharedRefresh,
      );
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [
    currentWorkspaceId,
    currentUserEmail,
    fetchDeveloperFeedbackCardsWithCredentials,
  ]);

  // Function to mark card as viewed
  const markCardAsViewed = (cardId: number) => {
    setViewedCards((prev) => {
      const newSet = new Set(prev);
      newSet.add(cardId);

      // Persist to localStorage
      localStorage.setItem(
        "viewed_developer_cards",
        JSON.stringify(Array.from(newSet)),
      );

      return newSet;
    });

    // ✅ ADD THIS BLOCK - Clear reshare marker when card is viewed
    localStorage.removeItem(`card_${cardId}_reshared`);

    // ✅ ADD THIS BLOCK - Update card state to remove reshare flag
    setDeveloperCards((prev) =>
      prev.map((card) =>
        card.id === cardId
          ? { ...card, isReshared: false, isRecentlyShared: false }
          : card,
      ),
    );

    console.log(`✅ Card ${cardId} marked as viewed, badges will be removed`);
  };

  /**
   * Handler to minimize workspace to Picture-in-Picture mode
   */
  /**
   * Handler to maximize workspace from Picture-in-Picture mode
   */
  const handleMaximizeFromPiP = useCallback(() => {
    if (!pipWorkspaceData) {
      console.warn("Cannot maximize: no PiP data available");
      return;
    }
    navigate(`/dashboard/workspace/${buildCardUrl(pipWorkspaceData.cardDbId, pipWorkspaceData.feedbackName)}`, {
      state: {
        pipRestore: {
          cardDbId: pipWorkspaceData.cardDbId,
          workspaceId: pipWorkspaceData.workspaceId,
          feedbackName: pipWorkspaceData.feedbackName,
          feedbackItems: pipWorkspaceData.feedbackItems,
          workspaceState: pipWorkspaceData.workspaceState,
        },
      },
    });
    setIsPiPMode(false);
    setPipWorkspaceData(null);
  }, [pipWorkspaceData, navigate]);

  /**
   * Handler to close Picture-in-Picture mode without restoring
   */
  // const handleClosePiP = useCallback(() => {
  //   // Optionally save draft before closing
  //   const shouldSaveDraft = window.confirm(
  //     "Do you want to save your work as a draft before closing?"
  //   );

  //   if (shouldSaveDraft && pipWorkspaceData) {
  //     // Restore state temporarily to save
  //     setCurrentFeedback(pipWorkspaceData.feedbackData);
  //     setCurrentApiResponse(pipWorkspaceData.apiResponse);
  //     setWorkspaceState(pipWorkspaceData.workspaceState);
  //     setFeedbackItems(pipWorkspaceData.feedbackItems);

  //     // Call the existing save draft handler
  //     handleSaveDraft(
  //       pipWorkspaceData.feedbackItems,
  //       pipWorkspaceData.workspaceState || undefined
  //     );
  //   }

  //   // Clear PiP state
  //   setIsPiPMode(false);
  //   setPipWorkspaceData(null);

  //   console.log("✅ PiP mode closed");
  // }, [pipWorkspaceData, handleSaveDraft]);

  // Render functions

 const renderMainContent = () => {
  // ✅ CHANGED: Allow rendering cards even when PiP is active
  if (showWorkspace || showViewFeedback || showUploadWorkspace) {
    console.log("Active view detected, rendering view instead of cards");
    return null;
  }

  // ✅ PiP mode should NOT prevent showing cards
  // The rest of the function remains the same...
  let cardsToShow: any[] = [];
  let hasCards = false;
  let isLoading = false;

  if (activeTab === "all") {
    cardsToShow = [...sharedFeedbackCards, ...developerCards, ...draftCards];
    hasCards = cardsToShow.length > 0;
    isLoading = isLoadingShared || isLoadingDeveloper || isLoadingDrafts;
  } else if (activeTab === "created") {
    cardsToShow = sharedFeedbackCards;
    hasCards = cardsToShow.length > 0;
    isLoading = isLoadingShared;
  } else if (activeTab === "draft") {
    cardsToShow = draftCards;
    hasCards = cardsToShow.length > 0;
    isLoading = isLoadingDrafts;
  } else if (activeTab === "shared") {
    cardsToShow = developerCards;
    hasCards = cardsToShow.length > 0;
    isLoading = isLoadingDeveloper;
  }

    // ✅ FIX: Check for cards FIRST - prioritize showing existing cards
    // This prevents the empty banner flash when cards are already loaded
    if (hasCards) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 3xl:grid-cols-5 4xl:grid-cols-6 ">
          {activeTab === "draft"
            ? draftCards.map((card) => (
                <CardHoverWrapper key={card.id}>
                  <DraftCard
                    imageUrl={card.imageUrl}
                    title={card.name}
                    feedbackCount={card.feedbackCount}
                    onEdit={() => handleEditDraftCard(card.id)}
                    onShare={() => handleShareDraftCard(card.id)}
                    onDelete={() => handleDeleteDraftCard(card.id)}
                    ownerName={currentUserName}
                    ownerProfile={profileImage || null}
                    lastUpdated={calculateDaysAgo(card.lastUpdated || card.created_at)}
                  />
                </CardHoverWrapper>
              ))
            : activeTab === "shared"
              ? developerCards.map((card) => (
                  <CardHoverWrapper key={card.id}>
                    <DeveloperFeedbackCard
                      appName={card.name}
                      feedbackCount={card.feedbackCount}
                      daysAgo={card.daysAgo}
                      imageUrl={card.imageUrl}
                      cardId={card.id}
                      preloadedDevelopers={card.preloadedDevelopers}
                      workspaceOwnerName={card.workspaceOwnerName}
                      workspaceOwnerProfile={card.workspaceOwnerProfile}
                      isNew={card.isRecentlyShared && !viewedCards.has(card.id)}
                      isReshared={card.isReshared}
                      onClick={() => handleViewSharedFeedback(card.id, false)}
                    />
                  </CardHoverWrapper>
                ))
              : activeTab === "all"
                ? (() => {
                    const combined = [
                      ...sharedFeedbackCards.map((c) => ({
                        _t: "shared" as const,
                        _d: new Date(c.sharedAt || c.created_at).getTime(),
                        c,
                      })),
                      ...developerCards.map((c) => ({
                        _t: "developer" as const,
                        _d: new Date(c.sharedAt || c.created_at).getTime(),
                        c,
                      })),
                      ...draftCards.map((c) => ({
                        _t: "draft" as const,
                        _d: new Date(c.lastUpdated || c.created_at).getTime(),
                        c,
                      })),
                    ].sort((a, b) => b._d - a._d);

                    return combined.map(({ _t, c }) => {
                      if (_t === "draft") {
                        const card = c as EnhancedDraftCardData;
                        return (
                          <CardHoverWrapper key={`draft-${card.id}`}>
                            <DraftCard
                              imageUrl={card.imageUrl}
                              title={card.name}
                              feedbackCount={card.feedbackCount}
                              onEdit={() => handleEditDraftCard(card.id)}
                              onShare={() => handleShareDraftCard(card.id)}
                              onDelete={() => handleDeleteDraftCard(card.id)}
                              ownerName={currentUserName}
                              ownerProfile={profileImage || null}
                              lastUpdated={calculateDaysAgo(card.lastUpdated || card.created_at)}
                            />
                          </CardHoverWrapper>
                        );
                      }
                      if (_t === "developer") {
                        const card = c as EnhancedSharedFeedbackCard;
                        return (
                          <CardHoverWrapper key={`dev-${card.id}`}>
                            <DeveloperFeedbackCard
                              appName={card.name}
                              feedbackCount={card.feedbackCount}
                              daysAgo={card.daysAgo}
                              imageUrl={card.imageUrl}
                              cardId={card.id}
                              preloadedDevelopers={card.preloadedDevelopers}
                              workspaceOwnerName={card.workspaceOwnerName}
                              workspaceOwnerProfile={card.workspaceOwnerProfile}
                              isNew={card.isRecentlyShared && !viewedCards.has(card.id)}
                              isReshared={card.isReshared}
                              onClick={() => handleViewSharedFeedback(card.id, false)}
                            />
                          </CardHoverWrapper>
                        );
                      }
                      const card = c as EnhancedSharedFeedbackCard;
                      return (
                        <CardHoverWrapper key={`shared-${card.id}`}>
                          <FeedbackCard
                            appName={card.name}
                            feedbackCount={card.feedbackCount}
                            daysAgo={card.daysAgo}
                            status={card.status}
                            imageUrl={card.imageUrl}
                            isNew={!viewedCards.has(card.id)}
                            cardId={card.id}
                            preloadedDevelopers={card.preloadedDevelopers}
                            workspaceOwnerName={card.workspaceOwnerName}
                            workspaceOwnerProfile={card.workspaceOwnerProfile}
                            onClick={() => handleViewSharedFeedback(card.id, false)}
                            onStatusChange={(newStatus) =>
                              handleFeedbackStatusChange(card.id, newStatus)
                            }
                            onDelete={() => handleDeleteSharedFeedbackCard(card.id)}
                          />
                        </CardHoverWrapper>
                      );
                    });
                  })()
                : sharedFeedbackCards.map((card) => (
                  <CardHoverWrapper key={card.id}>
                    <FeedbackCard
                      appName={card.name}
                      feedbackCount={card.feedbackCount}
                      daysAgo={card.daysAgo}
                      status={card.status}
                      imageUrl={card.imageUrl}
                      isNew={!viewedCards.has(card.id)}
                      cardId={card.id}
                      preloadedDevelopers={card.preloadedDevelopers}
                      workspaceOwnerName={card.workspaceOwnerName}
                      workspaceOwnerProfile={card.workspaceOwnerProfile}
                      onClick={() => handleViewSharedFeedback(card.id, false)}
                      onStatusChange={(newStatus) =>
                        handleFeedbackStatusChange(card.id, newStatus)
                      }
                      onDelete={() => handleDeleteSharedFeedbackCard(card.id)}
                    />
                  </CardHoverWrapper>
                ))}
        </div>
      );
    }

    // ✅ FIX: Only show loading skeleton when loading AND no cards exist yet
    // This prevents skeleton from showing when cards are already loaded
    if (isLoading && !hasCards) {
      return (
        <div className="py-6">
          <style>
            {`
          @keyframes shimmer {
            100% { background-position: -200% 0; }
            0% { background-position: 200% 0; }
          }
        `}
          </style>
          <CardGridSkeleton />
        </div>
      );
    }

    // ✅ FIX: Show empty state only when NOT loading AND no cards exist
    // This ensures empty banner only shows when truly empty
    return (
      <div className="min-h-[calc(100vh-380px)] flex items-center justify-center">
        <div className="max-w-3xl 3xl:max-w-4xl mx-auto w-full">
          <div
            className="rounded-2xl relative overflow-hidden min-w-[400px]"
            style={{
              background: "#EAF5FF",
            }}
          >
            <div className="pl-16 py-20 xl:py-24 2xl:py-24 3xl:py-28 4xl:py-32 text-left">
              <h2
                className="text-[32px]  font-medium text-[#192028] max-w-sm mb-2 leading-tight"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                {activeTab === "all"
                  ? "Create, collect & share your first feedback"
                  : activeTab === "draft"
                    ? "Create, collect & share your first feedback"
                    : "Create, collect & share your first feedback"}
              </h2>
              <p 
                className="text-[#192028] text-[14px] font-light max-w-md"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                {activeTab === "all"
                  ? "Bring clarity to ideas before they reach development."
                  : activeTab === "draft"
                    ? "Bring clarity to ideas before they reach development."
                    : "Bring clarity to ideas before they reach development."}
              </p>
            </div>
            <div className="absolute bottom-0 right-0 ">
              <Logo className="w-full h-36 2xl:w-full 2xl:h-48" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTabs = () => {
    const sharedCount = sharedFeedbackCards.length;
    const developerCount = developerCards.length;
    const allCount = sharedCount + developerCount + draftCards.length;

    const badgeClass = (isActive: boolean) =>
      `inline-flex items-center justify-center
  w-[20px] h-[20px]
  3xl:w-[24px] 3xl:h-[24px]
  4xl:w-[28px] 4xl:h-[28px]
  text-[10px] 3xl:text-xs 4xl:text-sm font-inter
  font-medium rounded-full transition-colors ${
    isActive
      ? "bg-[#53AEF4] text-white"
      : "bg-white text-[#192028] border-[0.5px] border-[#192028]"
  }`;

    const tabClass = (isActive: boolean) =>
      `pb-2 text-sm 3xl:text-[16px] 4xl:text-[18px] border-b-2 transition-colors flex items-center gap-1.5 ${
        isActive
          ? "text-[#192028] font-medium border-[#53AEF4]"
          : "text-[#192028] font-normal border-transparent"
      }`;

    return (
      <div className="bg-white border-b border-gray-200 mx-6">
        <div className="flex items-center justify-between">
          <div className="flex space-x-8 mt-12">
            <button
              onClick={() => handleTabSwitch("all")}
              className={tabClass(activeTab === "all")}
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              <span style={{ fontFamily: DEFAULT_FONT_FAMILY }}>All</span>
              <span className={badgeClass(activeTab === "all")}>
                {allCount > 99 ? "99+" : allCount}
              </span>
            </button>
            <button
              onClick={() => handleTabSwitch("created")}
              className={tabClass(activeTab === "created")}
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              <span style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Created by you</span>
              <span className={badgeClass(activeTab === "created")}>
                {sharedCount > 99 ? "99+" : sharedCount}
              </span>
            </button>
            <button
              onClick={() => handleTabSwitch("shared")}
              className={tabClass(activeTab === "shared")}
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              <span style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Shared files</span>
              <span className={badgeClass(activeTab === "shared")}>
                {developerCount > 99 ? "99+" : developerCount}
              </span>
            </button>
            <button
              onClick={() => handleTabSwitch("draft")}
              className={tabClass(activeTab === "draft")}
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              <span style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Draft</span>
              <span className={badgeClass(activeTab === "draft")}>
                {draftCards.length > 99 ? "99+" : draftCards.length}
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Loading state - only show on initial mount
  if (
    isInitializing &&
    currentWorkspaceId === null &&
    !initializationError
  ) {
    return <HomeScreenSkeleton />;
  }

  // Main HomeScreen render
  return (
    <div className="h-screen bg-white overflow-hidden flex flex-col">
      {/* Success Message Notification */}
      {showSuccessMessage && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p
                  className="text-sm font-medium text-green-800"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  {successMessageText.includes("deleted")
                    ? "Delete Successful!"
                    : "Share Successful!"}
                </p>
                <p
                  className="text-sm text-green-700 mt-1"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  {successMessageText}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0">
                <button
                  onClick={handleCloseSuccessMessage}
                  className="bg-green-50 rounded-md inline-flex text-green-400 hover:text-green-500 focus:outline-none"
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="px-6 pt-4 pb-2 bg-white flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <div
                className="text-sm text-[#11142D] font-semibold"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                <span className="font-normal">{getTimeBasedGreeting()},</span>{" "}
                {getDisplayName()}!
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="items-center flex flex-row">
              {/* <SearchIcon className="w-[24px] h-[24px] mr-4" />
              <span className="text-[#808191] font-medium text-sm mr-8" style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
  Search
</span> */}
              <img
                className="h-10 w-10 3xl:h-12 3xl:w-12 rounded-full object-cover"
                src={profileImage}
                alt="profile picture"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area - Scrollable */}
      <div className="flex-1 overflow-y-auto custom-scrollbar-overlay">
        {/* Create New Feedback Section */}
        <div className="px-6 relative">
          <h1
            className="text-2xl font-medium text-[#000000] mb-3"
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            Create new feedback
          </h1>

          {/* Grid for equal-sized buttons */}
          <div className="relative flex justify-between items-stretch max-w-3xl gap-4">
            {/* Web Application */}
            <button
              onClick={handleOpenPopup}
              className="bg-[#FFFEF5] border-[0.5px] border-[#E5D982] rounded-[12px] pl-5 py-5 pr-3 flex flex-col items-start justify-start hover:bg-[#FFFCE4] hover:border-[#E5D982] transition-colors min-h-[100px] flex-1 group"
            >
              <div className="w-8 h-8 relative mb-2">
                <Web className="w-8 h-8 absolute inset-0 transition-opacity duration-200 group-hover:opacity-0" />
                <AddWeb className="w-8 h-8 absolute inset-0 transition-opacity duration-200 opacity-0 group-hover:opacity-100" />
              </div>
              <span
                className="text-sm font-medium text-[#41444E]"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Web application
              </span>
            </button>

            {/* Mobile Application */}
            <button
              onClick={() => {
                setShowMobileAnimation(true);
                setMobileAnimationKey((prev) => prev + 1);
                setTimeout(() => setShowMobileAnimation(false), 2000); // Hide after animation completes
              }}
              className="bg-[#FDF6FF] border-[0.5px] border-[#FBC5F4] rounded-[12px] p-4 flex flex-col items-start justify-start hover:bg-[#FCF2FF] hover:border-[#FBC5F4] transition-colors min-h-[100px] flex-1 cursor-pointer  group relative"
            >
              <div
                className={`absolute -top-6 -right-10 w-36 h-36 pointer-events-none transition-opacity duration-300 z-50 ${
                  showMobileAnimation ? "opacity-100" : "opacity-0"
                }`}
              >
                <Lottie
                  key={mobileAnimationKey}
                  animationData={MobileAnimation}
                  loop={false}
                  autoplay={true}
                />
              </div>
              {/* Icon transition - still works on hover */}
              <div className="w-8 h-8 relative mb-2">
                <Mobile className="w-8 h-8 absolute inset-0 " />
                {/* <Mobile className="w-8 h-8 absolute inset-0 transition-opacity duration-200 group-hover:opacity-0" />
                <AddMobile className="w-8 h-8 absolute inset-0 transition-opacity duration-200 opacity-0 group-hover:opacity-100 text-[#912AE1]" /> */}
              </div>
              <span
                className="text-sm font-medium text-[#41444E]"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Mobile application
              </span>
              <span
                className="text-[10px] text-[#912AE1]"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Coming soon
              </span>
            </button>

            {/* Figma Design */}
            {/* <button
              onClick={() => {
                setShowFigmaAnimation(true);
                setFigmaAnimationKey((prev) => prev + 1);
                setTimeout(() => setShowFigmaAnimation(false), 2000); // Hide after animation completes
              }}
              className="bg-[#F7FFFD] border-[0.5px] border-[#A0E9D7] rounded-[12px] p-4 flex flex-col items-start justify-start hover:bg-[#EDFFFB] hover:border-[#A0E9D7] transition-colors min-h-[100px] flex-1 cursor-pointer group relative"
            >
              <div
                className={`absolute -top-6 -right-10 w-36 h-36 pointer-events-none transition-opacity duration-300 ${
                  showFigmaAnimation ? "opacity-100" : "opacity-0"
                }`}
              >
                <Lottie
                  key={figmaAnimationKey}
                  animationData={FigmaAnimation}
                  loop={false}
                  autoplay={true}
                />
              </div>
             
              <div className="w-8 h-8 relative mb-2">
                <Figma className="w-8 h-8 absolute inset-0 " />
                <Figma className="w-8 h-8 absolute inset-0 transition-opacity duration-200 group-hover:opacity-0" />
                <AddFigma className="w-8 h-8 absolute inset-0 transition-opacity duration-200 opacity-0 group-hover:opacity-100" />
              </div>
              <span className="text-sm font-medium text-[#41444E]"  style={{ fontFamily: DEFAULT_FONT_FAMILY }}>
                Figma design
              </span>
              <span className="text-[10px] text-[#42AD92] "  style={{ fontFamily: DEFAULT_FONT_FAMILY }}>Coming soon</span>
            </button> */}

            {/* Vertical Separator */}
            <div className="w-px bg-[#E9E9E9]  self-stretch my-1.5"></div>

            {/* Upload from System */}
            <button
              // onClick={handleOpenUploadPopup}
              className="bg-[#F8F9FBE5] border-[0.5px] border-[#A9A9A9] rounded-[12px] p-4 flex flex-col items-start justify-start hover:bg-[#F5F5F5E5] hover:border-[#A9A9A9] transition-colors min-h-[100px] flex-1 cursor-pointer group"
            >
              <div className="w-8 h-8 relative mb-2">
                <Upload className="w-8 h-8 absolute inset-0 " />
                {/* <Upload className="w-8 h-8 absolute inset-0 transition-opacity duration-200 group-hover:opacity-0" />
                <AddUpload className="w-8 h-8 absolute inset-0 transition-opacity duration-200 opacity-0 group-hover:opacity-100" /> */}
              </div>
              <span
                className="text-sm font-medium text-[#41444E]"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Upload from system
              </span>
              <span
                className="text-[10px] text-[#41444E]"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Coming soon
              </span>
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        {renderTabs()}

        {/* Main Content - Cards or Empty State */}
       <div 
  className="px-6 py-6"
  style={{
    overflowY: 'auto',
    overflowX: 'hidden',
    pointerEvents: 'auto'
  }}
  onWheel={(e) => {
    const container = e.currentTarget.parentElement; // Get the scrollable parent
    if (container) {
      container.scrollTop += e.deltaY;
    }
  }}
>
  {renderMainContent()}
</div>
      </div>

      {/* Popups */}
      <CreateNewWebApplicationPopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        onContinue={handleContinue}
        workspaceId={currentWorkspaceId || 1}
      />

      <CreateNewUplaodFromDevicePopup
        isOpen={isUploadPopupOpen}
        onClose={handleCloseUploadPopup}
        onContinue={handleUploadContinue} // ✅ Use new handler
        workspaceId={currentWorkspaceId || 1}
      />

      {/* Share Popup */}
      <ShareFeedbackPopup
        isOpen={isSharePopupOpen}
        onClose={handleSharePopupCancel}
        title={`Share "${
          currentShareCard?.name || "feedback"
        }" with developers`}
        totalFeedback={currentShareCard?.feedbackCount || 0}
        onShare={handleSharePopupSubmit}
        onCancel={handleSharePopupCancel}
        cardId={currentShareCard?.id}
      />

      {/* Picture-in-Picture Minimized Workspace */}
      {/* ✅ Picture-in-Picture - renders ON TOP of everything */}
    {isPiPMode && pipWorkspaceData && (
      <MinimizedWorkspace
        feedbackName={pipWorkspaceData.feedbackName}
        previewImage={pipWorkspaceData.previewImage}
        totalFeedbacks={pipWorkspaceData.feedbackItems.length}
        onMaximize={handleMaximizeFromPiP}
        onClose={() => {
          setIsPiPMode(false);
          setPipWorkspaceData(null);
          console.log("✅ PiP closed after successful save");

          window.dispatchEvent(new CustomEvent("refresh-draft-tab"));
          localStorage.setItem("draft_saved_timestamp", Date.now().toString());
        }}
        onSaveDraft={() => {
          if (pipWorkspaceData) {
            const firstScreenshot = pipWorkspaceData.feedbackItems.find(
              (item) => item.type === "screenshot" && item.annotatedImage,
            );
            setDraftCards((prev) =>
              prev.map((card) =>
                card.id === pipWorkspaceData.cardDbId
                  ? {
                      ...card,
                      feedbackCount: pipWorkspaceData.feedbackItems.length,
                      savedFeedbackItems: pipWorkspaceData.feedbackItems,
                      savedWorkspaceState: pipWorkspaceData.workspaceState || undefined,
                      lastUpdated: getCurrentISTTimestamp(),
                      imageUrl: firstScreenshot?.annotatedImage || card.imageUrl,
                    }
                  : card,
              ),
            );
            setSuccessMessageText(`"${pipWorkspaceData.feedbackName}" has been saved to drafts!`);
            setShowSuccessMessage(true);
            setTimeout(() => setShowSuccessMessage(false), 3000);
            pipStore.clear();
            setIsPiPMode(false);
            setPipWorkspaceData(null);
            setActiveTab("draft");
          }
        }}
      />
    )}
    <style>{`

      @keyframes shimmer {
        100% { background-position: -200% 0; }
        0% { background-position: 200% 0; }
      }

      @keyframes pulse-highlight {
        0%, 100% {
          background-color: #F7F9FE;
        }
        50% {
          background-color: #E3EDFF;
        }
      }

      .feedback-highlight-pulse {
        animation: pulse-highlight 2s ease-in-out 3;
      }
    `}</style>

      {showBetaPopup && (
        <BetaVersionPopup onDismiss={() => {
          setShowBetaPopup(false);
          markTourGuideDone(TOUR_GUIDE_KEYS.BETA_POPUP);
        }} />
      )}
  </div>
);
};

export default HomeScreen;
